import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { ElementModel, FilterModel } from '../Inbuilt-report-model';
import { ReportsService } from '../../reports.service';
import { ToastrService } from 'ngx-toastr';
import { debounceTime, Subject, takeUntil } from 'rxjs';
import {InbuiltUtilityService} from '../inbuilt-utility.service';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { DailogModalComponent } from '../../../shared/elements/dailog-modal/dailog-modal.component';
import { MatChipInputEvent } from '@angular/material/chips';
import { ENTER } from '@angular/cdk/keycodes';

type HierarchalCheck = { type: 'hierarchal'; paths: Array<Array<[string, string]>> };
type HierarchalDelimiterCheck = { type: 'hierarchal-delimiter'; paths: Array<Array<[string, string]>> };
type HierarchalExcludeCheck = { type: 'hierarchal-exclude'; paths: Array<Array<[string, string]>> };
type HierarchalDelimiterExcludeCheck = { type: 'hierarchal-delimiter-exclude'; paths: Array<Array<[string, string]>> };
type MultiselectExcludeCheck = { type: 'multiselect-exclude'; col: string; excludeSet: Set<string> };
type MultiselectCheck = { type: 'multiselect'; col: string; valueSet: Set<string> };
type FilterCheck =
  | HierarchalCheck
  | HierarchalDelimiterCheck
  | HierarchalExcludeCheck
  | HierarchalDelimiterExcludeCheck
  | MultiselectCheck
  | MultiselectExcludeCheck;

@Component({
  selector: 'app-inbuilt-filters-v2',
  standalone: false,
  templateUrl: './inbuilt-filters-v2.component.html',
  styleUrl: './inbuilt-filters-v2.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class InbuiltFiltersV2Component implements OnInit,OnChanges,OnDestroy {

  columnClasses: Record<number, string> = {
    1: 'col-12 col-sm-12 col-md-12 col-lg-1 col-xl-1 col-xxl-1',
    2: 'col-12 col-sm-12 col-md-12 col-lg-2 col-xl-2 col-xxl-2',
    3: 'col-12 col-sm-12 col-md-12 col-lg-3 col-xl-3 col-xxl-3',
    4: 'col-12 col-sm-12 col-md-12 col-lg-4 col-xl-4 col-xxl-4',
    5: 'col-12 col-sm-12 col-md-12 col-lg-5 col-xl-5 col-xxl-5',
    6: 'col-12 col-sm-12 col-md-12 col-lg-6 col-xl-6 col-xxl-6',
    7: 'col-12 col-sm-12 col-md-12 col-lg-7 col-xl-7 col-xxl-7',
    8: 'col-12 col-sm-12 col-md-12 col-lg-8 col-xl-8 col-xxl-8',
    9: 'col-12 col-sm-12 col-md-12 col-lg-9 col-xl-9 col-xxl-9',
    10: 'col-12 col-sm-12 col-md-12 col-lg-10 col-xl-10 col-xxl-10',
    11: 'col-12 col-sm-12 col-md-12 col-lg-11 col-xl-11 col-xxl-11',
    12: 'col-12 col-sm-12 col-md-12 col-lg-12 col-xl-12 col-xxl-12'
  };
  @Input() elementData: ElementModel | undefined;
  @Input() showView: boolean = false;
  @Input() config: any;
  @Output() onFiltersApplied: EventEmitter<any> = new EventEmitter();
  isApplyClicked: boolean = false;
  // True while applyFilters() is expanding large select-all-except selections (can take 1-2s).
  applyLoading: boolean = false;
  appliedSelectedFilters: any = {};
  filteredCascadeData: any = [];
  isAnyFilterSelected: boolean = false;
  isAnyFilterValueChanged: boolean = false;
  selectedFilters: any = {};
  allFilters: FilterModel[] = [];
  cascadeData : Array<any> = [];
  showJsonEditor = false;
  isJsonEditorPopupOpen = false;
  jsonEditorText = '';
  isJSONeditorEnabled = false;
  @ViewChild('jsonEditorPopup') jsonEditorPopup: any;

  // Free-text search chips (mat-chip input) + wild card / exact match toggle
  searchChipValues: string[] = [];
  /** Snapshot of searchChipValues as of the last Apply/Search click, used to detect chip changes since then. */
  private appliedSearchChipValues: string[] = [];
  isWildCardSearch: boolean = false;
  readonly searchChipSeparatorKeyCodes = [ENTER] as const;
  readonly maxSearchChipValues = 20;

  /** Filter JSON's optional `inputBoxSearch` config: per-mode label + columns for the free-text search box. */
  private get inputBoxSearchConfig(): any {
    return this.elementData?.element_json?.inputBoxSearch || null;
  }

  private get searchModeConfig(): any {
    const config = this.inputBoxSearchConfig;
    if (!config) return null;
    return (this.isWildCardSearch ? config.wildCardSearch : config.exactSearch) || null;
  }

  get searchFieldLabel(): string {
    return this.searchModeConfig?.label || 'Search';
  }

  get searchModeToggleLabel(): string {
    return this.isWildCardSearch ? 'Pattern Search' : 'Exact Search';
  }

  /** Columns to search against for the currently selected mode, taken from the filter JSON when `inputBoxSearch` is configured. */
  get searchColumns(): any {
    return this.searchModeConfig?.columns || null;
  }
   /** Shared normalization: strip spaces/underscores/hyphens → lowercase */
    private readonly norm = (v: string) => (v ?? '').toLowerCase().replace(/[\s_\-]+/g, '');
    private readonly isHierarchalType = (type: any) => {
      const value = String(type ?? '').trim().toLowerCase();
      return value === 'hierarchal' || value === 'hierarchal-delimiter';
    };
  
    private readonly fieldListSubject = new Subject<{ filterName: string; values: any; type: string; isUserInteraction: boolean }>();
    private readonly destroy$ = new Subject<void>();
    // Prevent programmatic downstream selection restores from recursively
    // re-triggering cascade rebuilds through setSelectedValues().
    private suppressCascadeSelectionEvents = false;
    /** Tracks filters that have already fired their first (initial) selection.
     *  Once a filter is in this set, any subsequent update to downstream filters
     *  should NOT trigger auto-selection via default_selection. */
    private readonly initialSelectionDone = new Set<string>();

  constructor(private reportsService: ReportsService, private toastr: ToastrService, private cdr: ChangeDetectorRef, private inbuiltUtilityService: InbuiltUtilityService, private dialog: MatDialog) { }

  private normalizeFilterMetadata(filters: FilterModel[]): FilterModel[] {
    return (filters ?? []).map((filter: any) => {
      const test = filter;
      const normalizedType = String(filter?.type ?? '').trim().toLowerCase();

      if (normalizedType === 'hierarchal') {
        const hierarchyColumns = this.getHierarchyColumnsFromFilter(filter);
        const hierarchalDisplayKey = (hierarchyColumns.displayKeys ?? [])
          .map((key: string) => String(key ?? '').trim())
          .filter(Boolean)
          .join(' : ');
        const hierarchalQueryKey = (hierarchyColumns.queryKeys ?? [])
          .map((key: string) => String(key ?? '').trim())
          .filter(Boolean)
          .join(' : ');

        if (hierarchalDisplayKey || hierarchalQueryKey) {
          // Keep the original filter.name key stable for selectedFilters lookup.
          filter.displayKey = hierarchalDisplayKey || String(filter?.name ?? '');
          filter.queryKey = hierarchalQueryKey || hierarchalDisplayKey || String(filter?.queryKey ?? filter?.name ?? '');
          return filter;
        }
      }

      const nameMeta = this.resolveObjectMapping(filter?.name);
      const queryMeta = this.resolveObjectMapping(filter?.queryKey);
      filter.name = nameMeta.displayKey;
      filter.queryKey = queryMeta.queryKey || nameMeta.queryKey;

      return filter;
    });
  }

  private resolveObjectMapping(raw: any): { displayKey: string; queryKey: string } {
    if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
      const entries = Object.entries(raw);
      if (entries.length > 0) {
        const [displayKey, queryKey] = entries[0];
        return {
          displayKey: String(displayKey ?? ''),
          queryKey: String(queryKey ?? displayKey ?? '')
        };
      }
    }

    const key = String(raw ?? '');
    return { displayKey: key, queryKey: key };
  }

  private resolveHierarchyPathMapping(raw: any): { displayPath: string; queryPath: string } {
    const pairs: Array<[string, string]> = [];

    const addPair = (display: any, query: any) => {
      const displayKey = String(display ?? '').trim();
      if (!displayKey) return;
      const queryKey = String(query ?? displayKey).trim() || displayKey;
      pairs.push([displayKey, queryKey]);
    };

    const addFromAny = (value: any) => {
      if (value == null) return;

      if (Array.isArray(value)) {
        value.forEach((item: any) => addFromAny(item));
        return;
      }

      if (typeof value === 'string') {
        const trimmed = value.trim();
        if (!trimmed) return;
        if ((trimmed.startsWith('{') && trimmed.endsWith('}')) || (trimmed.startsWith('[') && trimmed.endsWith(']'))) {
          try {
            addFromAny(JSON.parse(trimmed));
            return;
          } catch {
            // fall through to legacy colon handling
          }
        }
        trimmed.split(':').map((part: string) => part.trim()).filter(Boolean).forEach((part: string) => addPair(part, part));
        return;
      }

      if (typeof value === 'object') {
        Object.entries(value).forEach(([display, query]) => addPair(display, query));
      }
    };

    addFromAny(raw);

    const displayPath = pairs.map(([display]) => display).join(':');
    const queryPath = pairs.map(([, query]) => query).join(':');

    return {
      displayPath,
      queryPath: queryPath || displayPath
    };
  }

  getHierarchyDisplayPath(filter: any): string {
    const mapping = this.getHierarchyColumnsFromFilter(filter);
    return mapping.displayKeys.join(':');
  }

  getHierarchyDelimiterFilterKey(filter: any): string {
    return this.getHierarchyDelimiterQueryPath(filter) || this.getHierarchyDisplayPath(filter);
  }

  private getHierarchyQueryPath(filter: any): string {
    const mapping = this.getHierarchyColumnsFromFilter(filter);
    return mapping.queryKeys.join(':');
  }

  private getHierarchyDelimiterQueryPath(filter: any): string {
    const queryMeta = this.resolveObjectMapping(filter?.queryKey);
    const nameMeta = this.resolveObjectMapping(filter?.name);
    return String(queryMeta.queryKey || nameMeta.displayKey || '').trim();
  }

  private normalizeDelimiterLeafId(rawId: any): string {
    // Keep delimiter ids exactly as constructed in dropdown data.
    // For child nodes this is expected to be "parentId - childId".
    return String(rawId ?? '').trim();
  }

  private extractDelimiterPathNames(pathObj: any): string[] {
    if (!pathObj || typeof pathObj !== 'object' || Array.isArray(pathObj)) return [];
    return Object.keys(pathObj)
      .filter((key: string) => !key.startsWith('__'))
      .map((key: string) => String(pathObj[key] ?? '').trim())
      .filter(Boolean);
  }

  private isDelimiterNodeMatch(node: any, token: any): boolean {
    const normalizedToken = this.norm(String(token ?? ''));
    if (!normalizedToken) return false;

    const nodeName = this.norm(String(node?.name ?? ''));
    const rawId = String(node?.id ?? '').trim();
    const nodeId = this.norm(rawId);
    const nodeLeafId = this.norm(this.normalizeDelimiterLeafId(rawId));

    return normalizedToken === nodeName || normalizedToken === nodeId || normalizedToken === nodeLeafId;
  }

  private findDelimiterNodeByLeafId(nodes: any[], leafId: string): any | null {
    if (!Array.isArray(nodes) || !leafId) return null;

    const target = this.norm(leafId);
    const stack: any[] = [...nodes];

    while (stack.length > 0) {
      const node = stack.pop();
      if (!node) continue;

      const rawId = String(node?.id ?? '').trim();
      const rawNorm = this.norm(rawId);
      const leafNorm = this.norm(this.normalizeDelimiterLeafId(rawId));
      if (target === rawNorm || target === leafNorm) {
        return node;
      }

      if (Array.isArray(node.children) && node.children.length > 0) {
        stack.push(...node.children);
      }
    }

    return null;
  }

  private resolveDelimiterLeafIdFromPath(pathObj: any, dropdownData: any[]): string {
    const pathNames = this.extractDelimiterPathNames(pathObj);
    if (!pathNames.length || !Array.isArray(dropdownData) || dropdownData.length === 0) {
      return '';
    }

    let currentNodes: any[] = dropdownData;
    let matchedId = '';

    for (const pathName of pathNames) {
      const matchedNode = currentNodes.find((node: any) =>
        this.isDelimiterNodeMatch(node, pathName)
      );
      if (!matchedNode) {
        break;
      }

      matchedId = String(matchedNode?.id ?? '').trim();
      currentNodes = Array.isArray(matchedNode?.children) ? matchedNode.children : [];
    }

    return this.normalizeDelimiterLeafId(matchedId);
  }

  private getHierarchyColumnsFromFilter(filter: any): { displayKeys: string[]; queryKeys: string[] } {
    const displayKeys: string[] = [];
    const queryKeys: string[] = [];

    if (filter?.column && typeof filter.column === 'object' && !Array.isArray(filter.column)) {
      Object.entries(filter.column).forEach(([display, query]) => {
        const displayKey = String(display ?? '').trim();
        if (!displayKey) return;
        const queryKey = String(query ?? displayKey).trim() || displayKey;
        displayKeys.push(displayKey);
        queryKeys.push(queryKey);
      });

      if (displayKeys.length > 0) {
        return { displayKeys, queryKeys };
      }
    }

    const fallback = this.resolveHierarchyPathMapping(filter?.column);
    const fallbackDisplayParts = String(fallback.displayPath ?? '')
      .split(':')
      .map((part: string) => part.trim())
      .filter(Boolean);
    const fallbackQueryParts = String(fallback.queryPath ?? fallback.displayPath ?? '')
      .split(':')
      .map((part: string) => part.trim())
      .filter(Boolean);

    fallbackDisplayParts.forEach((displayPart: string, index: number) => {
      displayKeys.push(displayPart);
      queryKeys.push(fallbackQueryParts[index] || displayPart);
    });

    return { displayKeys, queryKeys };
  }

  private mapHierarchySelectionToQueryKeys(values: any[], displayPath: string, queryPath: string, dropdownData?: any[]): any[] {
    if (!Array.isArray(values) || values.length === 0) return [];

    const displayKeys = String(displayPath ?? '').split(':').map((k: string) => k.trim()).filter(Boolean);
    const queryKeys = String(queryPath ?? '').split(':').map((k: string) => k.trim()).filter(Boolean);

    const keyMap = new Map<string, string>();
    const normalizedKeyMap = new Map<string, string>();
    displayKeys.forEach((displayKey: string, index: number) => {
      const mappedKey = queryKeys[index] || displayKey;
      keyMap.set(displayKey, mappedKey);
      normalizedKeyMap.set(this.norm(displayKey), mappedKey);
    });

    const convertPath = (pathObj: any): any => {
      if (!pathObj || typeof pathObj !== 'object' || Array.isArray(pathObj)) return pathObj;
      const converted: any = {};

      // Cascade data is the ground truth — try it first because it always has
      // both display name column and ID column on the same row.
      const cascadeValueMap = this.resolveHierarchyQueryPathValuesFromCascade(pathObj, displayKeys, queryKeys);
      // Only fall back to dropdown-tree traversal when cascade lookup found nothing.
      const queryValueMap = Object.keys(cascadeValueMap).length > 0
        ? cascadeValueMap
        : this.resolveHierarchyQueryPathValues(pathObj, displayKeys, queryKeys, dropdownData || []);

      Object.keys(pathObj).forEach((pathKey: string) => {
        if (pathKey.startsWith('__')) {
          return;
        }
        const mappedKey = keyMap.get(pathKey) || normalizedKeyMap.get(this.norm(pathKey)) || pathKey;
        // Prefer explicit id values already present on the path object
        // (set during hierarchy selection enrichment) to avoid ambiguous
        // display-name lookups like duplicate "No Mapping" labels.
        const hasPreMappedValue = Object.prototype.hasOwnProperty.call(pathObj, mappedKey)
          && pathObj[mappedKey] !== undefined
          && pathObj[mappedKey] !== null
          && String(pathObj[mappedKey]).trim() !== '';
        if (hasPreMappedValue) {
          converted[mappedKey] = pathObj[mappedKey];
          return;
        }
        // Use resolved ID value; only keep original display name when both lookups fail.
        if (Object.prototype.hasOwnProperty.call(queryValueMap, mappedKey)) {
          converted[mappedKey] = queryValueMap[mappedKey];
        } else {
          converted[mappedKey] = pathObj[pathKey];
        }
      });
      return converted;
    };

    const allSentinel = values.find((v: any) => v?.__selectAll__ === true);
    if (allSentinel) {
      const excludePaths = Array.isArray(allSentinel.__excludePaths)
        ? allSentinel.__excludePaths.map((pathObj: any) => convertPath(pathObj))
        : undefined;
      return excludePaths && excludePaths.length > 0
        ? [{ __selectAll__: true, __excludePaths: excludePaths }]
        : [{ __selectAll__: true }];
    }

    return values.map((pathObj: any) => convertPath(pathObj));
  }

  private resolveHierarchyQueryPathValuesFromCascade(pathObj: any, displayKeys: string[], queryKeys: string[]): Record<string, any> {
    const result: Record<string, any> = {};
    if (!pathObj || typeof pathObj !== 'object' || !Array.isArray(this.cascadeData) || this.cascadeData.length === 0) {
      return result;
    }

    const normalizedPathObj = new Map<string, any>();
    Object.keys(pathObj || {}).forEach((key: string) => {
      normalizedPathObj.set(this.norm(key), pathObj[key]);
    });

    // Fast path: when every display key is present (the common case for fully-selected
    // leaf paths), use a pre-built Map index instead of a linear scan of cascadeData per
    // selected value — this is what made applyFilters slow for large hierarchical selections.
    const allDisplayValuesPresent = displayKeys.every((displayKey: string) => {
      const value = normalizedPathObj.get(this.norm(displayKey));
      return value !== undefined && value !== null && value !== '';
    });

    if (allDisplayValuesPresent) {
      const cascadeIndex = this.getOrBuildCascadeIndex(displayKeys, queryKeys);
      if (cascadeIndex) {
        const lookupKey = displayKeys
          .map((displayKey: string) => this.norm(String(normalizedPathObj.get(this.norm(displayKey)) ?? '')))
          .join('||');
        const matchingRow = cascadeIndex.rowsByKey.get(lookupKey);
        if (matchingRow) {
          for (let i = 0; i < displayKeys.length; i++) {
            const queryKey = queryKeys[i] || displayKeys[i];
            const rowQueryKey = cascadeIndex.rowKeyByQueryKey.get(queryKey);
            if (rowQueryKey) {
              result[queryKey] = matchingRow[rowQueryKey];
            }
          }
          return result;
        }
      }
    }

    // Slow path: partial/wildcard path objects (not every display key populated) fall back
    // to the original linear scan, since an exact-key index can't represent wildcards.
    const matchingRow = this.cascadeData.find((row: any) => {
      if (!row || typeof row !== 'object') {
        return false;
      }

      return displayKeys.every((displayKey: string) => {
        const selectedValue = normalizedPathObj.get(this.norm(displayKey));
        if (selectedValue === undefined || selectedValue === null || selectedValue === '') {
          return true;
        }

        const rowDisplayKey = Object.keys(row).find((k: string) => this.norm(k) === this.norm(displayKey));
        if (!rowDisplayKey) {
          return false;
        }

        return this.norm(String(row[rowDisplayKey] ?? '')) === this.norm(String(selectedValue ?? ''));
      });
    });

    if (!matchingRow) {
      return result;
    }

    for (let i = 0; i < displayKeys.length; i++) {
      const queryKey = queryKeys[i] || displayKeys[i];
      const rowQueryKey = Object.keys(matchingRow).find((k: string) => this.norm(k) === this.norm(queryKey));
      if (rowQueryKey) {
        result[queryKey] = matchingRow[rowQueryKey];
      }
    }

    return result;
  }

  /** Cache of a normalized-key → row index built once per (cascadeData, displayKeys/queryKeys) combo,
   *  so resolving many selected hierarchal paths against a large cascadeData is O(1) per lookup
   *  instead of O(cascadeData.length) per lookup. */
  private cascadeRowIndexCache?: {
    cascadeDataRef: Array<any>;
    keySignature: string;
    rowsByKey: Map<string, any>;
    rowKeyByQueryKey: Map<string, string>;
  };

  private getOrBuildCascadeIndex(
    displayKeys: string[],
    queryKeys: string[]
  ): { rowsByKey: Map<string, any>; rowKeyByQueryKey: Map<string, string> } | null {
    if (!Array.isArray(this.cascadeData) || this.cascadeData.length === 0) {
      return null;
    }

    const keySignature = `${displayKeys.join('||')}::${queryKeys.join('||')}`;
    const cached = this.cascadeRowIndexCache;
    if (cached && cached.cascadeDataRef === this.cascadeData && cached.keySignature === keySignature) {
      return cached;
    }

    const sampleRow = this.cascadeData.find((row: any) => row && typeof row === 'object') || {};
    const rowKeys = Object.keys(sampleRow);
    const rowKeyByDisplayKey = new Map<string, string>();
    const rowKeyByQueryKey = new Map<string, string>();
    displayKeys.forEach((displayKey: string) => {
      const match = rowKeys.find((k: string) => this.norm(k) === this.norm(displayKey));
      if (match) rowKeyByDisplayKey.set(displayKey, match);
    });
    queryKeys.forEach((queryKey: string) => {
      const match = rowKeys.find((k: string) => this.norm(k) === this.norm(queryKey));
      if (match) rowKeyByQueryKey.set(queryKey, match);
    });

    const rowsByKey = new Map<string, any>();
    if (rowKeyByDisplayKey.size === displayKeys.length) {
      for (const row of this.cascadeData) {
        if (!row || typeof row !== 'object') continue;
        const lookupKey = displayKeys
          .map((displayKey: string) => this.norm(String(row[rowKeyByDisplayKey.get(displayKey)!] ?? '')))
          .join('||');
        if (!rowsByKey.has(lookupKey)) {
          rowsByKey.set(lookupKey, row);
        }
      }
    }

    const result = { rowsByKey, rowKeyByQueryKey };
    this.cascadeRowIndexCache = { cascadeDataRef: this.cascadeData, keySignature, ...result };
    return result;
  }

  private resolveHierarchyQueryPathValues(pathObj: any, displayKeys: string[], queryKeys: string[], dropdownData: any[]): Record<string, any> {
    const result: Record<string, any> = {};
    if (!pathObj || typeof pathObj !== 'object' || !Array.isArray(dropdownData) || dropdownData.length === 0) {
      return result;
    }

    const normalizedPathObj = new Map<string, any>();
    Object.keys(pathObj || {}).forEach((key: string) => {
      normalizedPathObj.set(this.norm(key), pathObj[key]);
    });

    let currentNodes: any[] = dropdownData;
    for (let level = 0; level < displayKeys.length; level++) {
      const displayKey = displayKeys[level];
      const queryKey = queryKeys[level] || displayKey;
      const selectedName = normalizedPathObj.get(this.norm(displayKey));
      if (selectedName === undefined || selectedName === null) {
        break;
      }

      const matchedNode = currentNodes.find((node: any) =>
        this.norm(String(node?.name ?? '')) === this.norm(String(selectedName ?? ''))
      );
      if (!matchedNode) {
        break;
      }

      result[queryKey] = matchedNode?.value ?? matchedNode?.id ?? matchedNode?.name;
      currentNodes = Array.isArray(matchedNode?.children) ? matchedNode.children : [];
    }

    return result;
  }
  
  ngOnChanges(changes: SimpleChanges): void {
    if(changes['config'] && changes['config'].firstChange){
      const cachekey = `${this.config.reportNameFromQueryParams}_${this.config.currentPage ? this.config.currentPage.key : ''}_${this.config.currentTab ? this.config.currentTab.key : ''}_data_filter_filter`;
      const cachedObject = this.inbuiltUtilityService.getCachedData(cachekey);
      if(cachedObject){
        this.allFilters = this.normalizeFilterMetadata(cachedObject.allFilters || []);
        this.cascadeData = cachedObject.cascadeData;
        this.resetFilters();
      }else{
        this.getCascadeData();
      }
    }
  }

  ngOnInit(): void {
      this.inbuiltUtilityService.getJsonEditorToggleState()
        .pipe(takeUntil(this.destroy$))
        .subscribe((state: boolean) => {
          this.isJSONeditorEnabled = state;
          this.cdr.markForCheck();
        });
      this.fieldListSubject.pipe(
        debounceTime(300),
        takeUntil(this.destroy$)
      ).subscribe((payload) => {
        if (this.suppressCascadeSelectionEvents) {

          return;
        }

        this.checkIfAnyFilterSelected();
        if (payload.isUserInteraction) {
          // Show spinner on all downstream filters immediately while cascade rebuilds
          const changedIndex = this.allFilters.findIndex(f => f.name === payload.filterName);
          if (changedIndex !== -1) {
            this.allFilters.slice(changedIndex + 1)
              .filter(f => f.name.toLowerCase() !== 'attributes')
              .forEach(f => { f.isLoading = true; });
            this.cdr.detectChanges();
          }
        }
        setTimeout(() => {
          try {

          this.updateDropdownDataForFilter(payload.filterName, '', payload.isUserInteraction);
  
          }catch(e) {
              console.error('Error in delayed dropdown update:', e);
          }
          
        // 350ms: enough time for mat-select panel close animations (~250ms) to complete
        // before the heavy cascade processing starts, keeping the UI responsive.
        }, 100);
        
      });
  
    }
  
    ngOnDestroy() {
      this.destroy$.next();
      this.destroy$.complete();
    }

    onJsonEditorToggleChange(checked: boolean): void {
      this.showJsonEditor = checked;
      this.isJsonEditorPopupOpen = checked;

      if (checked) {
        this.jsonEditorText = JSON.stringify(this.buildSelectedFiltersForApply(), null, 2);
        this.jsonEditorPopup?.show?.();
        return;
      }

      this.jsonEditorPopup?.hide?.();
    }

    closeJsonEditor(): void {
      this.showJsonEditor = false;
      this.isJsonEditorPopupOpen = false;
    }

    openJsonEditor(): void {
      this.isJsonEditorPopupOpen = true;
      this.jsonEditorPopup?.show?.();
    }

    deleteReport() {
      const dialogConfig = new MatDialogConfig();
      dialogConfig.width = '400px';
      dialogConfig.hasBackdrop = true;
      dialogConfig.minHeight = '200px';
      dialogConfig.height = 'auto';
      dialogConfig.autoFocus = false;
      dialogConfig.data = {
        title: 'Delete Confirmation!',
        subTitle: 'Are you sure you want to delete this Element',
        buttonName: 'Continue'
      };

      const dialogRef = this.dialog.open(DailogModalComponent, dialogConfig);
      dialogRef.afterClosed().subscribe(result => {
        if (result && result.data !== 'cancel') {
          const reportId = this.elementData?.element_id ?? (this.elementData as any)?.id ?? null;
          const reportName = this.config?.reportNameFromQueryParams || (this.elementData as any)?.name || 'Report';
          const payload = {
            id: reportId,
            parent_id: this.config?.parentJSON?.id ?? null,
            parent_json: JSON.stringify(this.buildParentJsonWithElementRemoved())
          };
          this.reportsService.deleteReport(payload).subscribe({
            next: (_: any) => {
              this.toastr.success(`Visual has been deleted successfully`, '', {
                timeOut: 3000,
                positionClass: 'toast-top-center'
              });
              const report = this.config?.reportNameFromQueryParams ?? '';
        const pageKey = this.config?.currentPage?.key ?? '';
        const tabKey = this.config?.currentTab?.key ?? '';
        const filterCacheKey = `${this.config.reportNameFromQueryParams}_${this.config.currentPage ? this.config.currentPage.key : ''}_${this.config.currentTab ? this.config.currentTab.key : ''}_data_filter_filter`;
        this.inbuiltUtilityService.removeCachedData(filterCacheKey);
        this.inbuiltUtilityService.removeCachedData(`${report}_${pageKey}_${tabKey}_data_page`);
        this.inbuiltUtilityService.removeCachedData(`${report}_${pageKey}_${tabKey}_data_tab`);
        this.inbuiltUtilityService.triggerRefreshChildData();
            },
            error: (_: any) => {
              this.toastr.error('Error deleting report', '', {
                timeOut: 3000,
                positionClass: 'toast-top-center'
              });
            }
          });
        }
      });
    }

    /** Deep-clones the parent JSON (passed down from inbuilt-report-v2 via config.parentJSON) and drops
     *  this filter element from the current page/tab's elements array. */
    private buildParentJsonWithElementRemoved(): any {
      const cloned = JSON.parse(JSON.stringify(this.config?.parentJSON?.parent_json || {}));
      const pageKey = this.config?.currentPage?.key ?? '';
      const tabKey = this.config?.currentTab?.key ?? '';
      const tab = cloned?.pages?.[pageKey]?.tabs?.[tabKey];
      if (tab && Array.isArray(tab.elements)) {
        const elementName = String(this.elementData?.name ?? '');
        const elementType = String(this.elementData?.type ?? '').toLowerCase();
        tab.elements = tab.elements.filter((el: any) =>
          !(String(el?.name ?? '') === elementName && String(el?.type ?? '').toLowerCase() === elementType)
        );
      }
      return cloned;
    }

  getCascadeData(){ 
    this.elementData?.fields?.forEach(element => {
      element.dropdownData = [];
      element.isLoading = true;
    })
    this.allFilters = this.elementData?.fields || [];
    this.allFilters = this.normalizeFilterMetadata(this.elementData?.fields as FilterModel[] || []);
    const queryParams = { page: this.config.currentPage.key, tab: this.config.currentTab.key, report: this.config.reportNameFromQueryParams, elementName: this.elementData?.name ?? '', elementType: this.elementData?.type };
          this.reportsService.getInbuildReportFilters(queryParams).subscribe(data => {
            if(data){
              const attributeData = {
                column: data.column,
                default_selection: data.default_selection
              };
              this.cascadeData = data.cascadeData || [];
              this.allFilters = this.inbuiltUtilityService.setFilteredDropdownData(attributeData, this.cascadeData, this.allFilters);
              const cachekey = `${queryParams.report}_${queryParams.page}_${queryParams.tab}_${queryParams.elementName}_${queryParams.elementType}`;
              const object = {
                allFilters: this.allFilters,
                cascadeData: this.cascadeData
              };
              this.inbuiltUtilityService.cacheData(cachekey, object);
              this.cdr.detectChanges();
            } 
          });
        }

  handleErrors(error: any) {
    this.toastr.error('No Filter data found for the selected report.', 'Error', {
          timeOut: 5000, positionClass: 'toast-top-center'
    });  
  }

  /** Enrich each hierarchal path object with query-key → id pairs by traversing the
   *  dropdown tree so the stored value contains parent/child names and their raw ids. */
  private enrichHierarchalPathsWithIds(paths: any[], filterObj: any): any[] {
    if (!filterObj || !Array.isArray(paths)) return paths;
    const columns = this.getHierarchyColumnsFromFilter(filterObj);
    const displayKeys = columns.displayKeys;
    const queryKeys = columns.queryKeys;

    return paths.map((pathObj: any) => {
      if (!pathObj || typeof pathObj !== 'object' || Array.isArray(pathObj)) return pathObj;

      const enriched: any = { ...pathObj };
      const rawPathIds: string[] = Array.isArray(pathObj.__pathIds)
        ? pathObj.__pathIds.map((id: any) => String(id ?? '').trim())
        : [];
      let currentNodes: any[] = filterObj.dropdownData ?? [];

      for (let i = 0; i < displayKeys.length; i++) {
        const displayKey = displayKeys[i];
        const queryKey = queryKeys[i];
        const displayName = pathObj[displayKey] ?? pathObj[queryKey];
        if (displayName === undefined || displayName === null) break;

        if (pathObj[displayKey] === undefined) {
          enriched[displayKey] = displayName;
        }

        const levelPathId = rawPathIds[i];
        let matchedNode = levelPathId
          ? currentNodes.find((node: any) => String(node?.id ?? '').trim() === levelPathId)
          : null;

        if (!matchedNode) {
          matchedNode = currentNodes.find((node: any) =>
            String(node?.name ?? '').trim() === String(displayName).trim()
          );
        }
        if (!matchedNode) break;

        // Add query key → id only when it differs from the display key
        if (queryKey !== displayKey) {
          enriched[queryKey] = matchedNode.id;
        }

        currentNodes = Array.isArray(matchedNode.children) ? matchedNode.children : [];
      }

      delete enriched.__pathIds;

      return enriched;
    });
  }

  /** Enrich each hierarchal-delimiter path object with query-key → id pairs while
   *  normalizing child query values down to the child id segment only. */
  private enrichHierarchalDelimiterPathsWithIds(paths: any[], filterObj: any): any[] {
    if (!filterObj || !Array.isArray(paths)) return paths;
    const columns = this.getHierarchyColumnsFromFilter(filterObj);
    const displayKeys = columns.displayKeys;
    const queryKeys = columns.queryKeys;

    return paths.map((pathObj: any) => {
      if (!pathObj || typeof pathObj !== 'object' || Array.isArray(pathObj)) return pathObj;

      const enriched: any = { ...pathObj };
      const rawPathIds: string[] = Array.isArray(pathObj.__pathIds)
        ? pathObj.__pathIds.map((id: any) => String(id ?? '').trim())
        : [];
      let currentNodes: any[] = filterObj.dropdownData ?? [];

      for (let i = 0; i < displayKeys.length; i++) {
        const displayKey = displayKeys[i];
        const queryKey = queryKeys[i];
        const displayName = pathObj[displayKey] ?? pathObj[queryKey];
        if (displayName === undefined || displayName === null) break;

        if (pathObj[displayKey] === undefined) {
          enriched[displayKey] = displayName;
        }

        const levelPathId = rawPathIds[i];
        let matchedNode = levelPathId
          ? currentNodes.find((node: any) => String(node?.id ?? '').trim() === levelPathId)
          : null;

        if (!matchedNode) {
          matchedNode = currentNodes.find((node: any) =>
            String(node?.name ?? '').trim() === String(displayName).trim()
          );
        }
        if (!matchedNode) break;

        if (queryKey !== displayKey) {
          const rawId = String(matchedNode.id ?? '').trim();
          if (i === 0 || !rawId.includes(' - ')) {
            enriched[queryKey] = rawId;
          } else {
            const idParts = rawId.split(' - ').map((part: string) => part.trim()).filter(Boolean);
            enriched[queryKey] = idParts.length > 0 ? idParts[idParts.length - 1] : rawId;
          }
        }

        currentNodes = Array.isArray(matchedNode.children) ? matchedNode.children : [];
      }

      delete enriched.__pathIds;

      return enriched;
    });
  }

  private buildHierarchalDelimiterSelection(paths: any[], filterObj: any): { selectedValues: any[] } {
    const enrichedPaths = this.enrichHierarchalDelimiterPathsWithIds(paths, filterObj);
    const columns = this.getHierarchyColumnsFromFilter(filterObj);
    const queryKeys = columns.queryKeys;
    const selectedValues: any[] = [];
    const seen = new Set<string>();
    const delimiterQueryKey = String(
      this.getHierarchyDelimiterQueryPath(filterObj) ||
      queryKeys[queryKeys.length - 1] ||
      filterObj?.queryKey ||
      ''
    ).trim();
    if (!delimiterQueryKey) {
      return { selectedValues };
    }

    for (const pathObj of enrichedPaths) {
      if (!pathObj || typeof pathObj !== 'object' || Array.isArray(pathObj)) continue;
      const resolvedLeafId = this.resolveDelimiterLeafIdFromPath(pathObj, filterObj?.dropdownData ?? []);
      const rawFallbackId = String(pathObj[delimiterQueryKey] ?? '').trim();
      const normalizedFallbackId = this.normalizeDelimiterLeafId(rawFallbackId);
      let fallbackId = '';

      // If fallback is composite "parent - child", keep only child segment.
      if (rawFallbackId.includes(' - ')) {
        fallbackId = normalizedFallbackId;
      } else if (normalizedFallbackId) {
        // Accept non-composite fallback only when it points to a leaf node.
        // This avoids passing parent ids for delimiter filters.
        const matchedNode = this.findDelimiterNodeByLeafId(filterObj?.dropdownData ?? [], normalizedFallbackId);
        const isLeafNode = !!matchedNode && (!Array.isArray(matchedNode.children) || matchedNode.children.length === 0);
        if (isLeafNode) {
          fallbackId = normalizedFallbackId;
        }
      }

      const finalId = resolvedLeafId || fallbackId;
      if (finalId && !seen.has(finalId)) {
        seen.add(finalId);
        selectedValues.push({ [delimiterQueryKey]: finalId });
      }
    }

    return { selectedValues };
  }

  private prepareHierarchalSelection(event: any[], filterObj: any): { values: any[] } {
    const allSentinel = event.find((v: any) => v?.__selectAll__ === true);
    if (allSentinel) {
      const excludes = Array.isArray(allSentinel.__excludePaths) ? allSentinel.__excludePaths : undefined;
      const enrichedExcludes = excludes ? this.enrichHierarchalPathsWithIds(excludes, filterObj) : undefined;
      return {
        values: enrichedExcludes
          ? [{ __selectAll__: true, __excludePaths: enrichedExcludes }]
          : [{ __selectAll__: true }]
      };
    }

    return { values: this.enrichHierarchalPathsWithIds(event, filterObj) };
  }

  private prepareHierarchalDelimiterSelection(event: any[], filterObj: any): { values: any[] } {
    const allSentinel = event.find((v: any) => v?.__selectAll__ === true);
    if (allSentinel) {
      const excludes = Array.isArray(allSentinel.__excludePaths) ? allSentinel.__excludePaths : undefined;
      const normalizedExcludes = excludes ? this.buildHierarchalDelimiterSelection(excludes, filterObj).selectedValues : [];

      return {
        values: normalizedExcludes.length > 0
          ? [{ __selectAll__: true, __excludePaths: normalizedExcludes }]
          : [{ __selectAll__: true }]
      };
    }

    const delimiterSelection = this.buildHierarchalDelimiterSelection(event, filterObj);
    return {
      values: delimiterSelection.selectedValues
    };
  }

  private prepareMultiselectSelection(event: any[]): { values: any[] } {
    const allSentinel = event.find((v: any) => v?.__selectAll__ === true);
    if (allSentinel) {
      const excludes = Array.isArray(allSentinel.__excludeNames) ? allSentinel.__excludeNames : undefined;
      return {
        values: excludes ? [{ __selectAll__: true, __excludeNames: excludes }] : [{ __selectAll__: true }]
      };
    }

    return { values: event };
  }

  private getHierarchalSelectionKeys(filterObj: any): { displayKey: string; queryKey: string } {
    const hierarchyKeySeparator = ' : ';
    const columns = this.getHierarchyColumnsFromFilter(filterObj);
    const displayKey = columns.displayKeys.join(hierarchyKeySeparator);
    const queryKey = columns.queryKeys.join(hierarchyKeySeparator);

    if (displayKey || queryKey) {
      return {
        displayKey: displayKey || String(filterObj?.displayKey ?? filterObj?.name ?? ''),
        queryKey: queryKey || displayKey || String(filterObj?.queryKey ?? filterObj?.name ?? '')
      };
    }

    const fallbackDisplayKey = String(filterObj?.displayKey ?? filterObj?.name ?? '');
    return {
      displayKey: fallbackDisplayKey,
      queryKey: String(filterObj?.queryKey ?? fallbackDisplayKey)
    };
  }

  private buildSelectedFilterEntry(event: any, filterObj: any, type: string): any {
    const normalizedType = String(type ?? '').trim().toLowerCase();

    if (this.isHierarchalType(normalizedType) && event?.length > 0) {
      if (normalizedType === 'hierarchal-delimiter') {
        const selection = this.prepareHierarchalDelimiterSelection(event, filterObj);
        return { values: selection.values, type: normalizedType };
      }

      const hierarchalKeys = this.getHierarchalSelectionKeys(filterObj);
      const selection = this.prepareHierarchalSelection(event, filterObj);
      return {
        values: selection.values,
        type: normalizedType,
        displayKey: hierarchalKeys.displayKey,
        queryKey: hierarchalKeys.queryKey
      };
    }

    if (normalizedType === 'multiselect' && event?.length > 0) {
      const selection = this.prepareMultiselectSelection(event);
      return { values: selection.values, type: normalizedType };
    }

    const selectedFilterEntry: any = { values: event, type: normalizedType || type };
    return selectedFilterEntry;
  }

  setSelectedValues(event: any, filter: any, type: string) {
    const normalizedType = String(type ?? '').trim().toLowerCase();
    // isUserInteraction is true only when this filter has already completed its initial
    // selection (i.e. it's firing again due to a user change, not initial load or reset).
    const isUserInteraction = this.initialSelectionDone.has(filter);
    this.initialSelectionDone.add(filter);
    const filterObj = this.allFilters.find((f: any) => f.name === filter);
    if(event && event.length == 0){
      if (filterObj) {
        filterObj.isSelectedAll = false;
      }
    }
    // Keep compact sentinel payloads for large select-all selections.
    // hierarchal: [{__selectAll__:true}] or [{__selectAll__:true,__excludePaths:[...]}]
    // multiselect: [{__selectAll__:true}] or [{__selectAll__:true,__excludeNames:[...]}]
    // to avoid O(paths × rows) work on large datasets.
    // Do NOT mutate filterObj.isSelectedAll here on partial changes.
    // Changing this input from true -> false causes hierarchy-dropdown ngOnChanges
    // to call deselectAllNodes(), which wipes the user's remaining selections.
    const selectedFilterEntry = this.buildSelectedFilterEntry(event, filterObj, normalizedType);
    this.selectedFilters[filter] = selectedFilterEntry;
    if (this.suppressCascadeSelectionEvents) {
      // Keep local selection state updated but do not trigger another cascade
      // rebuild while updateDropdownDataForFilter is already in progress.
      this.checkIfAnyFilterSelected();
      return;
    }
    this.fieldListSubject.next({ filterName: filter, values: selectedFilterEntry.values, type: selectedFilterEntry.type, isUserInteraction });
  }

  checkIfAnyFilterSelected() {
    this.isAnyFilterSelected = false;

    // All keys that have at least one value selected (exclude 'attributes' — column selector only).
    const filtersWithValues = Object.keys(this.selectedFilters).filter(
      key => key !== 'attributes' &&
             Array.isArray(this.selectedFilters[key]?.values) &&
             this.selectedFilters[key].values.length > 0
    );

    // Partition into none-only and real-value filter keys in one pass.
    const noneFilterKeys = new Set<string>();
    const realFilterKeys: string[] = [];
    for (const key of filtersWithValues) {
      if (this.selectedFilters[key].values.some((v: any) => String(v.name ?? '').toLowerCase() === 'none')) {
        noneFilterKeys.add(key);
      } else {
        realFilterKeys.push(key);
      }
    }

    // Apply is enabled when at least one non-None filter has a selection, or when the free-text
    // search box has one or more values entered.
    const hasSearchChipValues = this.searchChipValues.length > 0;
    this.isAnyFilterSelected = realFilterKeys.length > 0 || hasSearchChipValues;
    if(noneFilterKeys.size > 0) {
      this.isAnyFilterSelected = false;
      return;
    }

    if (this.isApplyClicked) {
      this.checkIfAnyFilterValueChanged(realFilterKeys);
    }

    this.cdr.detectChanges();
  }

  // Compares selectedFilters with appliedSelectedFilters to determine if any filter values have changed since the last apply.
  checkIfAnyFilterValueChanged(realFilterKeys: string[]) {
    // Union of real current keys + previously applied keys to catch additions AND removals.
    const allKeys = new Set([
      ...realFilterKeys,
      ...Object.keys(this.appliedSelectedFilters)
    ]);

    for (const key of allKeys) {
      const selectedValues: any[] = this.selectedFilters[key]?.values ?? [];
      const appliedValues: any[]  = this.appliedSelectedFilters[key]?.values ?? [];

      if (selectedValues.length !== appliedValues.length) {
        this.isAnyFilterValueChanged = true;
        return;
      }

      // Sort both arrays by their canonical JSON representation before comparing,
      // so order differences in the arrays don't cause false positives.
      const canonSort = (a: any, b: any) => (JSON.stringify(a) < JSON.stringify(b) ? -1 : 1);
      const sortedSelected = JSON.stringify([...selectedValues].sort(canonSort));
      const sortedApplied  = JSON.stringify([...appliedValues].sort(canonSort));

      if (sortedSelected !== sortedApplied) {
        this.isAnyFilterValueChanged = true;
        return;
      }
    }

    // Dropdown filters are unchanged — fall back to comparing the free-text search chips.
    const sortedCurrentChips = JSON.stringify([...this.searchChipValues].sort());
    const sortedAppliedChips = JSON.stringify([...this.appliedSearchChipValues].sort());
    this.isAnyFilterValueChanged = sortedCurrentChips !== sortedAppliedChips;
  }

  addSearchChip(event: MatChipInputEvent): void {
    this.addSearchChipValues(event.value ?? '');
    event.chipInput?.clear();
  }

  /** Pasted text (e.g. from Notepad) is split on newlines so each line becomes its own chip. */
  onSearchChipPaste(event: ClipboardEvent, input: HTMLInputElement): void {
    const text = event.clipboardData?.getData('text') ?? '';
    if (!text) return;
    event.preventDefault();
    this.addSearchChipValues(text);
    input.value = '';
  }

  /** Commits whatever the user typed but didn't press Enter for, once the field loses focus. */
  onSearchChipInputBlur(input: HTMLInputElement): void {
    this.addSearchChipValues(input.value ?? '');
    input.value = '';
  }

  /** Splits on newlines (paste from another line-based source counts each line as a separate value) and enforces the max cap. */
  private addSearchChipValues(raw: string): void {
    const lines = raw.split(/\r\n|\r|\n/).map(line => line.trim()).filter(line => line.length > 0);
    if (lines.length === 0) return;
    let limitReached = false;
    for (const line of lines) {
      if (this.searchChipValues.length >= this.maxSearchChipValues) {
        limitReached = true;
        break;
      }
      this.searchChipValues.push(line);
    }
    if (limitReached) {
      this.toastr.warning(`You can add a maximum of ${this.maxSearchChipValues} search values.`, '', {
        timeOut: 3000,
        positionClass: 'toast-top-center'
      });
    }
    this.checkIfAnyFilterSelected();
    this.cdr.markForCheck();
  }

  removeSearchChip(chip: string): void {
    this.searchChipValues = this.searchChipValues.filter(c => c !== chip);
    this.checkIfAnyFilterSelected();
    this.cdr.markForCheck();
  }

  onSearchModeToggleChange(checked: boolean): void {
    this.isWildCardSearch = checked;
     if (this.isApplyClicked && this.searchChipValues.length > 0) {
       this.isAnyFilterValueChanged = true;
     }
     this.cdr.markForCheck();
  }

  selectAllFilters() {
    //Reset the data 
    // Logic to select all filter values and update the report data accordingly
    this.allFilters.forEach(filter => {
      // if(filter && filter.selectedCount != filter.dropdownData.length){  
        filter.isSelectedAll = false;
        filter.selectedCount = 0;
        filter.default_selection = filter.initial_default_selection;
        filter.resetCounter = (filter.resetCounter || 0) + 1;
        filter.dropdownData = filter.initialData;
      // }
    });
    setTimeout(() => {
      this.allFilters.forEach(filter => {
        filter.isSelectedAll = true;
    });
    this.cdr.detectChanges();

    }, 100); // Let the UI update with all unselected first, for better UX
    
    // this.checkIfAnyFilterSelected();
    // Run a synchronous CD cycle so child output emissions (selectionChange/selectedCountChange)
    // are fully flushed before Angular's zone-triggered cycle checks template expressions.
    // Without this, isAnyFilterSelected/isAnyFilterValueChanged change *after* Angular has
    // already evaluated the [disabled] binding, causing ExpressionChangedAfterItHasBeenCheckedError.
    this.cdr.detectChanges();
  }

  applyFilters() {
    this.isApplyClicked = true;
    this.applyLoading = true;
    // Force the loader to paint before the potentially heavy (1-2s) synchronous
    // expansion of large select-all-except selections runs below.
    this.cdr.detectChanges();
    setTimeout(() => this.runApplyFilters(), 0);
  }

  private runApplyFilters(): void {
    let isNoneValueExists = false;
    const arr = this.allFilters.filter((val)=>val.default_selection == 'None');
    // let isNoneValueExists = false;
    // let noneValueFilterName = '';
    // if(arr.length > 0){
    //   arr.forEach(val=>{
    //     if(this.selectedFilters[val.name] && this.selectedFilters[val.name].values && this.selectedFilters[val.name].values.length > 0){
    //       if(this.selectedFilters[val.name].values.some((v: any) => v.name && v.name.toLowerCase() == 'none')){
    //         isNoneValueExists = true;
    //         noneValueFilterName = val.name;
    //       }

    //     }
    //   })
    // }
    // const columns: Array<{ field: string , tooltipField: string }> = [];
    // if(this.selectedFilters['attributes'] && this.selectedFilters['attributes'].values && this.selectedFilters['attributes'].values.length > 0 ){
    //   const isSelectAll = this.selectedFilters['attributes'].values.some((v: any) => v?.__selectAll__ === true);
    //   if(isSelectAll){
    //     columns.push(...this.allFilters.find((filter: any) => filter.name === 'attributes')?.dropdownData.map((option: any) => option.name) || []);
    //   }else{
    //     this.selectedFilters['attributes'].values.forEach((attribute: any) => {
    //       columns.push(attribute.name);
    //     });
    //   }
    // }else{
    //   const arr = this.allFilters.filter((filter: any) => filter.name === 'attributes');
    //   if(arr.length > 0){
    //     arr[0].dropdownData.forEach((option: any) => {
    //       columns.push(option.name);
    //     });
    //   }
    // }
    const selectedFiltersForApply = this.buildSelectedFiltersForApply();
    // Logic to apply selected filters and update the report data accordingly
    // if(columns.length > 0) {
   const Config = {
        // columns: columns,
        filterName: this.elementData?.name,
        reportNameFromQueryParams: this.config?.reportNameFromQueryParams,
        currentPage: this.config?.currentPage,
        currentTab: this.config?.currentTab,
        showTable: true,
        selectedFilters: selectedFiltersForApply,
        isNoneValueExists: isNoneValueExists,
        inputSearchFilter: this.buildInputSearchFilterQuery()
      };
      this.inbuiltUtilityService.publishSelectedFilterEvent(Config);
      this.onFiltersApplied.emit(Config);
    // }
    
    this.isAnyFilterValueChanged = false;
    this.appliedSelectedFilters = JSON.parse(JSON.stringify(this.selectedFilters));
    this.appliedSearchChipValues = [...this.searchChipValues];
    this.applyLoading = false;
    this.cdr.detectChanges();
  }

  /** Builds `OR (col LIKE '%v%' OR ...)` (wildcard) or `OR (col = 'v' OR ...)` (exact) across all configured columns/chip values, sanitizing every value. */
  private buildInputSearchFilterQuery(): string {
    const columns = Object.values(this.searchColumns || {})
      .map((col: any) => this.sanitizeSqlIdentifier(col))
      .filter((col: string) => !!col);
    const values = (this.searchChipValues || [])
      .map(value => this.sanitizeSqlValue(value))
      .filter((value: string) => !!value);

    if (columns.length === 0 || values.length === 0) return '';

    const conditions: string[] = [];
    columns.forEach((column: string) => {
      values.forEach((value: string) => {
        conditions.push(this.isWildCardSearch ? `${column} LIKE '%${value}%'` : `${column} = '${value}'`);
      });
    });

    return conditions.length > 0 ? `(${conditions.join(' OR ')})` : '';
  }

  /** Escapes single quotes and strips statement-injection/comment sequences from a value bound into a raw SQL fragment. */
  private sanitizeSqlValue(value: any): string {
    return String(value ?? '')
      .trim()
      .replace(/'/g, "''")
      .replace(/;/g, '')
      .replace(/--/g, '')
      .replace(/\/\*/g, '')
      .replace(/\*\//g, '');
  }

  /** Column names come from the report's own filter JSON, not user input, but are still restricted to a safe identifier charset. */
  private sanitizeSqlIdentifier(value: any): string {
    return String(value ?? '').trim().replace(/[^a-zA-Z0-9_ ]/g, '');
  }

  private buildSelectedFiltersForApply(): any {
    const result: any = {};

    for (const filter of this.allFilters as any[]) {
      this.applySelectedFilterEntry(filter, result, {
        includeHierarchalDisplayEntry: false,
        includeSameKeyDelimiterEntry: true
      });
    }

    return result;
  }
  /**
   * Applies the selected filter entry to the result object.
   * Handles hierarchal, delimiter, and multiselect filter types.
   */
  private applySelectedFilterEntry(
    filter: any,
    result: any,
    options: { includeHierarchalDisplayEntry?: boolean; includeSameKeyDelimiterEntry?: boolean } = {}
  ): void {
    const selectedFilterLookupKey = String(filter.name ?? '');
    const hierarchalKeys = String(filter.type ?? '').trim().toLowerCase() === 'hierarchal'
      ? this.getHierarchalSelectionKeys(filter)
      : null;
    const displayKey = hierarchalKeys?.displayKey || selectedFilterLookupKey;
    const queryKey = hierarchalKeys?.queryKey || String(filter.queryKey ?? displayKey);
    const entry = this.selectedFilters[selectedFilterLookupKey];
    const values = this.resolveFilterValuesForApply(filter, entry);
    if (values.length === 0) return;

    const normalizedFilterType = String(filter.type ?? '').trim().toLowerCase();
    const { displayPath, queryPath } = this.getDisplayAndQueryPaths(filter, normalizedFilterType);

    const isHierarchalFilter = this.isHierarchalType(filter.type);
    const payloadEntry: any = { values, type: filter.type, displayKey, queryKey, displayPath, queryPath };
    if (!isHierarchalFilter || options.includeHierarchalDisplayEntry !== false) {
      result[displayKey] = payloadEntry;
    }

    if (normalizedFilterType === 'hierarchal-delimiter') {
      // Delimiter selections are already normalized to leaf child ids in
      // buildHierarchalDelimiterSelection(). Do not run generic hierarchy
      // remapping here, otherwise values can be overwritten (for example,
      // with parent-level ids from cascade lookup fallbacks).
      this.applyDelimiterQueryKeyEntry(
        result,
        payloadEntry,
        displayKey,
        queryKey,
        options.includeSameKeyDelimiterEntry === true
      );
      return;
    }

    if (this.isHierarchalType(filter.type)) {
      this.applyHierarchalQueryKeyEntry(
        result,
        filter,
        payloadEntry,
        displayKey,
        queryKey,
        values,
        options.includeHierarchalDisplayEntry !== false
      );
    } else if (queryKey && queryKey !== displayKey) {
      result[queryKey] = { ...payloadEntry, values };
    }
  }

  private getDisplayAndQueryPaths(filter: any, normalizedFilterType: string): { displayPath: string; queryPath: string } {
    if (!this.isHierarchalType(filter.type)) {
      const path = String(filter.column ?? '');
      return { displayPath: path, queryPath: path };
    }
    const queryPath = normalizedFilterType === 'hierarchal-delimiter'
      ? this.getHierarchyDelimiterQueryPath(filter)
      : this.getHierarchyQueryPath(filter);

    return { displayPath: this.getHierarchyDisplayPath(filter), queryPath };
  }

  private applyDelimiterQueryKeyEntry(
    result: any,
    payloadEntry: any,
    displayKey: string,
    queryKey: string,
    includeSameKey = false
  ): void {
    if (queryKey && (includeSameKey || queryKey !== displayKey)) {
      result[queryKey] = { ...payloadEntry, values: payloadEntry.values };
    }
  }

  // For hierarchal filters, always resolve IDs regardless of whether the top-level queryKey
  // matches the displayKey. The column-level queryPath (e.g. "LOB_ID:SubLOB_ID") holds the real payload keys.
  private applyHierarchalQueryKeyEntry(
    result: any,
    filter: any,
    payloadEntry: any,
    displayKey: string,
    queryKey: string,
    values: any[],
    includeDisplayEntry = true
  ): void {
    const queryValues = this.mapHierarchySelectionToQueryKeys(
      values,
      payloadEntry.displayPath,
      payloadEntry.queryPath,
      filter.dropdownData
    );

    const idMappedEntry = { ...payloadEntry, isQueryMappedEntry: true, values: queryValues };
    // Overwrite the display-key entry so getTablePayload always sees ID values.
    if (includeDisplayEntry) {
      result[displayKey] = idMappedEntry;
    }
    if (queryKey && (includeDisplayEntry ? queryKey !== displayKey : true)) {
      result[queryKey] = idMappedEntry;
    }
  }

  private resolveFilterValuesForApply(filter: any, entry: any): any[] {
    const values: any[] = entry?.values ?? [];
    if (values.length > 0) {
      const allSentinel = values.find((v: any) => v?.__selectAll__ === true);
      if (allSentinel) {
        const hasExcludeNames = Array.isArray(allSentinel?.__excludeNames) && allSentinel.__excludeNames.length > 0;
        const hasExcludePaths = Array.isArray(allSentinel?.__excludePaths) && allSentinel.__excludePaths.length > 0;
        if (!hasExcludeNames && !hasExcludePaths) {
          return [{ __selectAll__: true }];
        }
        return this.expandPlainSelectAllForApply(filter, allSentinel, values);
      }
      return values;
    }

    if (!entry) {
      return this.resolveFilterValuesFromDefaultSelection(filter);
    }

    return [];
  }

  private expandPlainSelectAllForApply(filter: any, allSentinel: any, fallbackValues: any[]): any[] {
    const hasExcludeNames = Array.isArray(allSentinel?.__excludeNames) && allSentinel.__excludeNames.length > 0;
    const hasExcludePaths = Array.isArray(allSentinel?.__excludePaths) && allSentinel.__excludePaths.length > 0;
    if (hasExcludeNames && !this.isHierarchalType(filter.type)) {
      const excludedNames = new Set<string>(
        allSentinel.__excludeNames.map((name: any) => this.norm(String(name ?? '')))
      );
      return (filter.dropdownData ?? []).filter((item: any) => !excludedNames.has(this.norm(String(item?.name ?? ''))));
    }

    if (hasExcludePaths && this.isHierarchalType(filter.type)) {
      // Match on query keys/ids only — display names can repeat across different parents
      // (e.g. duplicate "No Mapping" labels), so name-based matching can exclude the wrong
      // leaves. buildHierarchyLeafPayload() now returns leaves keyed by query key with id
      // values, matching what enrichHierarchalPathsWithIds() attaches to exclude entries.
      const allHierarchyLeaves = this.buildHierarchyLeafPayload(filter);
      // hierarchal-delimiter filters normally have no "column" mapping (they use
      // name/queryKey + splitClause instead), so getHierarchyQueryPath() is empty for
      // them — fall back to the single delimiter query key in that case.
      const isDelimiterWithoutColumn = String(filter.type ?? '').trim().toLowerCase() === 'hierarchal-delimiter'
        && this.getHierarchyColumnsFromFilter(filter).queryKeys.length === 0;
      const queryKeySet = new Set(
        (isDelimiterWithoutColumn ? [this.getHierarchyDelimiterQueryPath(filter)] : this.getHierarchyQueryPath(filter).split(':'))
          .map((k: string) => this.norm(k.trim()))
          .filter(Boolean)
      );
      const normalizePathEntries = (obj: any): Array<[string, string]> =>
        Object.keys(obj ?? {})
          .filter((key) => !key.startsWith('__') && queryKeySet.has(this.norm(key)))
          .map((key) => [this.norm(key), String(obj[key] ?? '').trim().toLowerCase()] as [string, string]);

      // An excluded path can be a partial (parent-level) path — unchecking a parent node
      // only records that parent's own key, not the full key set of every descendant leaf —
      // so a leaf must be excluded whenever ALL of an exclude entry's keys match it, not
      // only when the two paths are an exact, same-length match.
      const excludedPathEntries = allSentinel.__excludePaths.map((pathObj: any) => normalizePathEntries(pathObj));

      return allHierarchyLeaves.filter((pathObj: any) => {
        const leafEntries = new Map(normalizePathEntries(pathObj));
        return !excludedPathEntries.some((excludeEntries: Array<[string, string]>) =>
          excludeEntries.length > 0 && excludeEntries.every(([key, value]) => leafEntries.get(key) === value)
        );
      });
    }

    // For mismatched payload/type combinations, keep existing value payload unchanged.
    if (hasExcludeNames || hasExcludePaths) return fallbackValues;

    if (this.isHierarchalType(filter.type)) {
      return this.buildHierarchyLeafPayload(filter);
    }

    return Array.isArray(filter.dropdownData) ? [...filter.dropdownData] : [];
  }

  private resolveFilterValuesFromDefaultSelection(filter: any): any[] {
    const raw = filter.default_selection;
    if (!raw || raw === 'None') return [];

    if (raw === 'All') {
      if (this.isHierarchalType(filter.type)) {
        return this.buildHierarchyLeafPayload(filter);
      }
      return Array.isArray(filter.dropdownData) ? [...filter.dropdownData] : [];
    }

    if (typeof raw === 'string' && raw.startsWith('[')) {
      try {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed) && parsed.length > 0) {
          const allSentinel = parsed.find((v: any) => v?.__selectAll__ === true);
          if (allSentinel) {
            return this.expandPlainSelectAllForApply(filter, allSentinel, parsed);
          }
          return parsed;
        }
      } catch {
        return [];
      }
    }

    if (!this.isHierarchalType(filter.type) && typeof raw === 'string') {
      const names = new Set(
        raw.split(',')
          .map((value: string) => this.norm(value))
          .filter(Boolean)
      );
      return (filter.dropdownData ?? []).filter((item: any) => names.has(this.norm(String(item?.name ?? ''))));
    }

    return [];
  }

  private buildDelimiterHierarchyLeafPayload(filter: any): any[] {
    const delimiterQueryKey = String(this.getHierarchyDelimiterQueryPath(filter) || filter?.queryKey || '').trim();
    if (!delimiterQueryKey || !Array.isArray(filter.dropdownData)) return [];

    const result: any[] = [];
    const walk = (nodes: any[]) => {
      for (const node of nodes ?? []) {
        if (Array.isArray(node?.children) && node.children.length > 0) {
          walk(node.children);
          continue;
        }
        const id = this.normalizeDelimiterLeafId(node?.id ?? node?.name);
        if (id) result.push({ [delimiterQueryKey]: id });
      }
    };
    walk(filter.dropdownData);
    return result;
  }

  private buildHierarchyLeafPayload(filter: any): any[] {
    const displayPath = this.getHierarchyDisplayPath(filter);
    const keyParts: string[] = displayPath ? displayPath.split(':').map((k: string) => k.trim()) : [];
    if (!keyParts.length) {
      // hierarchal-delimiter filters normally have no "column" mapping (they use
      // name/queryKey + splitClause instead), so there are no column-derived keys to
      // build leaves from — fall back to a single delimiter key built from the tree.
      if (String(filter?.type ?? '').trim().toLowerCase() === 'hierarchal-delimiter') {
        return this.buildDelimiterHierarchyLeafPayload(filter);
      }
      return [];
    }
    if (!Array.isArray(filter.dropdownData)) return [];

    const queryPath = this.getHierarchyQueryPath(filter);
    const queryKeyParts: string[] = queryPath ? queryPath.split(':').map((k: string) => k.trim()) : [];

    const result: any[] = [];
    // Value per level is the node's query id when one exists, falling back to its display
    // name only when the level has no distinct id (query key === display key).
    const walk = (nodes: any[], path: any[]) => {
      for (const node of nodes ?? []) {
        const nextPath = [...path, node];
        if (node?.children?.length) {
          walk(node.children, nextPath);
          continue;
        }

        const entry: any = {};
        nextPath.forEach((n: any, index: number) => {
          const key = queryKeyParts[index] ?? keyParts[index] ?? `${filter.name} S${index}`;
          entry[key] = n?.id ?? n?.name;
        });
        result.push(entry);
      }
    };

    walk(filter.dropdownData, []);
    return result;
  }

  /** Set of normalized query-key ids reachable at each level of a hierarchal filter's
   *  (freshly rebuilt) dropdownData — used to drop stale selections by id instead of by
   *  display name. Index 0 is the root level (e.g. "Visibility"), index 1 the next level, etc. */
  private buildHierarchalValidLeafKeySetsByLevel(f: any): Set<string>[] {
    const isDelimiter = String(f?.type ?? '').toLowerCase() === 'hierarchal-delimiter';

    if (isDelimiter) {
      const leafIds = new Set<string>();
      const walk = (nodes: any[]) => {
        for (const node of nodes ?? []) {
          if (node?.children?.length) {
            walk(node.children);
          } else {
            leafIds.add(this.norm(String(node?.id ?? '')));
          }
        }
      };
      walk(f?.dropdownData ?? []);
      return [leafIds];
    }

    const queryKeyParts = this.getHierarchyColumnsFromFilter(f).queryKeys;
    if (queryKeyParts.length === 0) return [];

    const setsByLevel = queryKeyParts.map(() => new Set<string>());
    for (const leaf of this.buildHierarchyLeafPayload(f)) {
      queryKeyParts.forEach((key: string, index: number) => {
        setsByLevel[index].add(this.norm(String(leaf?.[key] ?? '')));
      });
    }
    return setsByLevel;
  }

  /** Keeps only the previously-selected hierarchal path objects whose query key/id still
   *  exists in the rebuilt dropdownData, matching by id instead of display name.
   *  Only the root level (e.g. "Visibility") needs to still exist — requiring every deeper
   *  level to also match exactly is too strict when those levels don't have a stable id
   *  column in the cascade data, which was wiping out valid root-level selections. */
  private filterHierarchalValuesStillPresent(f: any, prevValues: any[]): any[] {
    if (!Array.isArray(prevValues) || prevValues.length === 0) return prevValues;

    const setsByLevel = this.buildHierarchalValidLeafKeySetsByLevel(f);
    if (setsByLevel.length === 0 || setsByLevel[0].size === 0) return [];

    const isDelimiter = String(f?.type ?? '').toLowerCase() === 'hierarchal-delimiter';
    if (isDelimiter) {
      const delimiterQueryKey = String(
        this.getHierarchyDelimiterQueryPath(f) || f?.queryKey || f?.name || ''
      ).trim();
      return prevValues.filter((v: any) => setsByLevel[0].has(this.norm(String(v?.[delimiterQueryKey] ?? ''))));
    }

    const rootQueryKey = this.getHierarchyColumnsFromFilter(f).queryKeys[0];
    if (!rootQueryKey) return prevValues;
    return prevValues.filter((v: any) => setsByLevel[0].has(this.norm(String(v?.[rootQueryKey] ?? ''))));
  }

  /** Reset a single filter from the label-row reset icon */
  clearSingleFilter(filter: any) {
    filter.isSelectedAll = false;
    filter.selectedCount = 0;
    filter.resetCounter = (filter.resetCounter || 0) + 1;
    filter.default_selection = filter.default_selection || filter.initial_default_selection;
    delete this.selectedFilters[filter.name];
    this.updateDropdownDataForFilter(filter.name,'clear');
    this.checkIfAnyFilterSelected();
  }

  resetFilters() {
    // Logic to reset filters to default values and update the report data accordingly
    this.allFilters.forEach(filter => {
      filter.isSelectedAll = false;
      filter.selectedCount = 0;
      filter.default_selection = filter.initial_default_selection;
      // Increment resetCounter so hierarchy-dropdown's ngOnChanges always fires,
      // even when isSelectedAll was already false (false→false is not a change).
      filter.resetCounter = (filter.resetCounter || 0) + 1;
      // Use same reference so that on the next unfiltered fast-path, f.dropdownData === f.initialData
      // and Angular OnPush skips buildMaps in hierarchy-dropdown.
      filter.dropdownData = filter.initialData;
    });
    this.selectedFilters = {};
    this.initialSelectionDone.clear(); // Allow default_selection to work again after full reset
    this.isAnyFilterSelected = false;
    this.isAnyFilterValueChanged = false;
    this.isApplyClicked = false;

    if (this.searchChipValues.length > 0) {
      this.searchChipValues = [];
    }
    this.appliedSearchChipValues = [];

    const config = {
      filterName: this.elementData?.name,
      reportNameFromQueryParams: this.config?.reportNameFromQueryParams,
      currentPage: this.config?.currentPage,
      currentTab: this.config?.currentTab,
      showTable: false,
      selectedFilters: {},
      isNoneValueExists: false,
      inputSearchFilter: this.buildInputSearchFilterQuery()
    };
    this.inbuiltUtilityService.publishSelectedFilterEvent(config);
    this.onFiltersApplied.emit(config);
  }

  //Function that will skip array of objects till filterName and filter data after wards
  updateDropdownDataForFilter(filterName: string, action: string = '', isUserInteraction: boolean = false): void {
    this.suppressCascadeSelectionEvents = true;
    const releaseCascadeSuppression = () => {
      this.suppressCascadeSelectionEvents = false;
    };

    const normFilterName = this.norm(filterName);
    const filterIndex = this.allFilters.findIndex(f => this.norm(f.name) === normFilterName);
    const upstreamFilterNames = new Set(
      this.allFilters.slice(0, filterIndex < 0 ? this.allFilters.length : filterIndex + 1)
        .map(f => f.name)
    );
    this.filterCascadeDataForFields(upstreamFilterNames);

    if (filterIndex === -1 || filterIndex + 1 >= this.allFilters.length) {
      releaseCascadeSuppression();
      return;
    }

    const downstream = this.allFilters.slice(filterIndex + 1).filter(
      f => f.name.toLowerCase() !== 'attributes'
    );
    if (downstream.length === 0) {
      releaseCascadeSuppression();
      return;
    }

    const data = this.filteredCascadeData;
    const previousHierarchyState = this.captureHierarchalSelections(downstream, action, isUserInteraction);
    this.resetDownstreamFilters(downstream);

    if (!data || data.length === 0) {
      this.clearDownstreamFilters(downstream, action, isUserInteraction);
      releaseCascadeSuppression();
      return;
    }

    const multiselectFilters = downstream.filter(f => !this.isHierarchalType(f.type));
    const hierarchalFilters = downstream.filter(f => this.isHierarchalType(f.type));
    const isDataUnfiltered = data.length === this.cascadeData.length;
    this.restoreMultiselectFilters(multiselectFilters, isDataUnfiltered, data, action, isUserInteraction);
    this.cdr.detectChanges();
    this.restoreHierarchalFilters(
      hierarchalFilters,
      data,
      action,
      isUserInteraction,
      isDataUnfiltered,
      previousHierarchyState,
      releaseCascadeSuppression
    );
  }

  private captureHierarchalSelections(
    downstream: FilterModel[],
    action: string,
    isUserInteraction: boolean
  ): { values: Map<string, any[]>; isAll: Map<string, boolean> } {
    const values = new Map<string, any[]>();
    const isAll = new Map<string, boolean>();
    if (action === 'clear' || !isUserInteraction) return { values, isAll };

    downstream.filter(f => this.isHierarchalType(f.type)).forEach(f => {
      const selectedValues = this.selectedFilters[f.name]?.values ?? [];
      const sentinel = selectedValues[0];
      const hasExcludePaths = !!sentinel?.__selectAll__
        && Array.isArray(sentinel?.__excludePaths)
        && sentinel.__excludePaths.length > 0;
      const selectedAll = (!!f.isSelectedAll && selectedValues.length === 0)
        || (!!sentinel?.__selectAll__ && !hasExcludePaths);
      isAll.set(f.name, selectedAll);
      if (!selectedAll && selectedValues.length > 0) {
        values.set(f.name, [...selectedValues]);
      }
    });

    return { values, isAll };
  }

  private resetDownstreamFilters(downstream: FilterModel[]): void {
    downstream.forEach(filter => {
      filter.resetCounter = (filter.resetCounter || 0) + 1;
      filter.selectedCount = 0;
      filter.isSelectedAll = false;
    });
  }

  private clearDownstreamFilters(downstream: FilterModel[], action: string, isUserInteraction: boolean): void {
    downstream.forEach(filter => {
      filter.dropdownData = [];
      filter.isLoading = false;
      // if (action !== 'clear' && isUserInteraction) {
      //   filter.default_selection = '';
      // }
       // Do NOT blank default_selection here: with dropdownData empty, resolveFilterValuesFromDefaultSelection()
      // already resolves 'All' to [] (same as ''), so clearing it only wiped the visible "All" label to blank
      // with no functional benefit - this was the cause of downstream filters losing their default label.
    });
  }

  private restoreHierarchalFilters(
    filters: FilterModel[],
    data: any[],
    action: string,
    isUserInteraction: boolean,
    isDataUnfiltered: boolean,
    previousState: { values: Map<string, any[]>; isAll: Map<string, boolean> },
    releaseCascadeSuppression: () => void
  ): void {
    const processFilter = (index: number): void => {
      if (index >= filters.length) {
        releaseCascadeSuppression();
        return;
      }

      const filter = filters[index];
      this.buildHierarchalDropdown(filter, data, isDataUnfiltered);
      filter.isLoading = false;
      this.restoreHierarchalSelection(filter, action, isUserInteraction, previousState);

      this.cdr.detectChanges();
      setTimeout(() => processFilter(index + 1), 0);
    };

    setTimeout(() => processFilter(0), 0);
  }

  private buildHierarchalDropdown(filter: FilterModel, data: any[], isDataUnfiltered: boolean): void {
    if (filter.default_selection === 'None') {
      filter.default_selection = 'All';
      filter.isSelectedAll = true;
      return;
    }
    if (isDataUnfiltered && filter.initialData?.length > 0) {
      filter.dropdownData = filter.initialData;
      return;
    }

    if (String(filter.type ?? '').toLowerCase() === 'hierarchal-delimiter') {
      filter.dropdownData = this.inbuiltUtilityService.buildTreeForHierarchyDelimiter(
        filter,
        data,
        String(filter?.splitClause ?? '')
      );
      return;
    }

    const hierarchyColumns = this.getHierarchyColumnsFromFilter(filter);
    filter.dropdownData = this.buildHierarchyTree(
      data,
      hierarchyColumns.displayKeys,
      hierarchyColumns.queryKeys
    );
  }

  private restoreHierarchalSelection(
    filter: FilterModel,
    action: string,
    isUserInteraction: boolean,
    previousState: { values: Map<string, any[]>; isAll: Map<string, boolean> }
  ): void {
    if (action === 'clear') {
      filter.default_selection = '';
      return;
    }
    if (!isUserInteraction) return;
    if (previousState.isAll.get(filter.name)) {
      filter.default_selection = 'All';
      return;
    }

    const previousValues = previousState.values.get(filter.name) ?? [];
    const allSentinel = previousValues.find((value: any) => value?.__selectAll__ === true);
    if (allSentinel && Array.isArray(allSentinel.__excludePaths)) {
      filter.default_selection = JSON.stringify([
        { __selectAll__: true, __excludePaths: allSentinel.__excludePaths }
      ]);
      return;
    }

    const stillPresentValues = this.filterHierarchalValuesStillPresent(filter, previousValues);
    filter.default_selection = stillPresentValues.length > 0
      ? JSON.stringify(stillPresentValues)
      : '';
  }

  private restoreMultiselectFilters(multiselectFilters: FilterModel[], isDataUnfiltered: boolean, data: any[], action: string, isUserInteraction: boolean): void {
    if (isDataUnfiltered) {
      this.restoreMultiselectFiltersFast(multiselectFilters, data, action, isUserInteraction);
    } else {
      this.restoreMultiselectFiltersFiltered(multiselectFilters, data, action, isUserInteraction);
    }
  }

  // Fast path: restore all downstream filters from initialData (O(filters), not O(rows)).
  // Use the SAME initialData reference (no spread) so Angular's OnPush change detection
  // sees no input change for hierarchy-dropdown → buildMaps never re-runs on 50k+ nodes.
  private restoreMultiselectFiltersFast(
    multiselectFilters: FilterModel[],
    data: any[],
    action: string,
    isUserInteraction: boolean
  ): void {
    let runningData = data;
    let canReuseInitialData = true;

    for (const f of multiselectFilters as any[]) {
      const previousValues: any[] = this.selectedFilters[f.name]?.values ?? [];
      const newDropdown = canReuseInitialData
        ? (f.initialData?.length > 0 ? f.initialData : [])
        : this.buildMultiselectDropdown(f, runningData);
      f.dropdownData = newDropdown;
      f.isLoading = false;
      const restoredValues = this.restoreMultiselectSelection(
        f,
        previousValues,
        newDropdown,
        action,
        isUserInteraction
      );

      const narrowedData = this.narrowRowsBySiblingSelection(runningData, f, restoredValues);
      canReuseInitialData = canReuseInitialData && narrowedData === runningData;
      runningData = narrowedData;
    }
  }

  // Filtered path: rebuild each multiselect from progressively narrowed rows so
  // earlier sibling selections constrain later dropdowns.
  private restoreMultiselectFiltersFiltered(multiselectFilters: FilterModel[], data: any[], action: string, isUserInteraction: boolean): void {
    let runningData = data;
    for (const f of multiselectFilters as any[]) {
      const previousValues: any[] = this.selectedFilters[f.name]?.values ?? [];
      const newDropdown = this.buildMultiselectDropdown(f, runningData);
      f.dropdownData = newDropdown;
      f.isLoading = false;
      const restoredValues = this.restoreMultiselectSelection(
        f,
        previousValues,
        newDropdown,
        action,
        isUserInteraction
      );

      runningData = this.narrowRowsBySiblingSelection(runningData, f, restoredValues);
    }
  }

  private buildMultiselectDropdown(filter: any, data: any[]): any[] {
    if (!Array.isArray(data) || data.length === 0) return [];

    const firstRowKeys = Object.keys(data[0]);
    const normalizedName = this.norm(filter.name);
    const nameKey = firstRowKeys.find(key => this.norm(key) === normalizedName);
    if (!nameKey) return [];

    const configuredQueryKey = String(filter.queryKey ?? '').trim();
    const valueKey = firstRowKeys.find(key => this.norm(key) === this.norm(configuredQueryKey))
      || firstRowKeys.find(key => this.norm(key) === normalizedName + 'id')
      || nameKey;
    const seen = new Set<string>();
    const result: any[] = [];

    for (const row of data) {
      const value = row[nameKey];
      if (value === undefined || value === null) continue;

      const normalizedValue = String(value);
      if (!seen.has(normalizedValue)) {
        seen.add(normalizedValue);
        result.push({ name: value, value: row[valueKey] });
      }
    }

    return result;
  }

  private restoreMultiselectSelection(
    filter: any,
    previousValues: any[],
    newDropdown: any[],
    action: string,
    isUserInteraction: boolean
  ): any[] {
    if (action === 'clear' || !isUserInteraction || previousValues.length === 0 || newDropdown.length === 0) {
      if (action === 'clear') {
        filter.default_selection = '';
      }
      return [];
    }

    const allSentinel = previousValues.find((value: any) => value?.__selectAll__ === true);
    if (allSentinel) {
      const excludeNames = Array.from(new Set(
        (Array.isArray(allSentinel.__excludeNames) ? allSentinel.__excludeNames : [])
          .map((name: any) => this.norm(String(name ?? '')))
          .filter(Boolean)
      ));
      if (excludeNames.length === 0) {
        filter.default_selection = 'All';
        return [{ __selectAll__: true }];
      }

      filter.default_selection = JSON.stringify([{ __selectAll__: true, __excludeNames: excludeNames }]);
      return [{ __selectAll__: true, __excludeNames: excludeNames }];
    }

    const newValueSet = new Set(
      newDropdown.map((item: any) => this.norm(String(item.value ?? item.name ?? '')))
    );
    const stillPresent = previousValues.filter((value: any) =>
      newValueSet.has(this.norm(String(value.value ?? value.name ?? '')))
    );
    filter.default_selection = stillPresent.length > 0
      ? stillPresent.map((value: any) => String(value.name)).join(',')
      : '';
    return stillPresent;
  }

  // Shared comparison helper used by tests and by callers that only need the restored text.
  private resolveMultiselectDefaultSelectionRestore(prevValues: any[], newDropdown: any[]): string {
    if (prevValues.length === 0 || newDropdown.length === 0) {
      return '';
    }

    const allSentinel = prevValues.find((v: any) => v?.__selectAll__ === true);
    if (allSentinel) {
      const excludeNames = new Set<string>(
        (Array.isArray(allSentinel.__excludeNames) ? allSentinel.__excludeNames : [])
          .map((name: any) => this.norm(String(name ?? '')))
      );
      if (excludeNames.size === 0) {
        return 'All';
      }
      // Keep restore payload compact for very large lists (e.g. 18k Application values).
      return JSON.stringify([{ __selectAll__: true, __excludeNames: Array.from(excludeNames) }]);
    }

    const newNameSet = new Set(newDropdown.map((item: any) => this.norm(String(item.name ?? ''))));
    const stillPresent = prevValues
      .filter((v: any) => newNameSet.has(this.norm(String(v.name ?? ''))))
      .map((v: any) => String(v.name));
    return stillPresent.length > 0 ? stillPresent.join(',') : '';
  }

  // Builds a deduplicated hierarchy tree from flat cascade data.
  // keyParts: colon-split segments of filter.column — each is the direct column name for that level.
  // Node ids are path-composite ("root||child||grandchild") so identical values under
  // different parents never collide in hierarchy-dropdown's parentMap / nodeMap.
  private buildHierarchyTree(cascadeData: any[], keyParts: string[], queryKeyParts: string[]): any[] {
    if (!keyParts || keyParts.length === 0) return [];

    // Pre-resolve actual column names once from the first item that has them,
    // instead of calling Object.keys().find() for every row at every level.
    const resolvedCols: (string | undefined)[] = new Array(keyParts.length).fill(undefined);
    const resolvedValueCols: (string | undefined)[] = new Array(keyParts.length).fill(undefined);
    for (const item of cascadeData) {
      for (let i = 0; i < keyParts.length; i++) {
        if (resolvedCols[i] === undefined) {
          resolvedCols[i] = Object.keys(item).find(k => this.norm(k) === this.norm(keyParts[i]));
        }
        if (resolvedValueCols[i] === undefined) {
          const queryKey = queryKeyParts[i] || keyParts[i];
          resolvedValueCols[i] = Object.keys(item).find(k => this.norm(k) === this.norm(queryKey));
        }
      }
      if (resolvedCols.every(c => c !== undefined)) break;
    }

    const roots: any[] = [];
    // nodeMap: nodeId → node — O(1) lookup replaces O(n) Array.find() at every level
    const nodeMap = new Map<string, any>();

    for (const item of cascadeData) {
      const rootCol = resolvedCols[0];
      if (!rootCol || !item[rootCol]) continue;

      const rootName: string = item[rootCol];
      const rootValueCol = resolvedValueCols[0] || rootCol;
      const rootId = String(item[rootValueCol] ?? rootName);
      // nodeMapKey stays composite to guarantee uniqueness across siblings with same id
      const rootMapKey = rootId;

      if (!nodeMap.has(rootMapKey)) {
        const rootNode = { name: rootName, id: rootId };
        roots.push(rootNode);
        nodeMap.set(rootMapKey, rootNode);
      }

      let parentMapKey = rootMapKey;

      for (let level = 1; level < keyParts.length; level++) {
        const col = resolvedCols[level];
        if (!col || !item[col]) break;

        const childName: string = item[col];
        const childValueCol = resolvedValueCols[level] || col;
        const childRawId = String(item[childValueCol] ?? childName);
        const childMapKey = `${parentMapKey}||${childRawId}`;

        if (!nodeMap.has(childMapKey)) {
          const childNode = { name: childName, id: childRawId };
          nodeMap.set(childMapKey, childNode);
          const parentNode = nodeMap.get(parentMapKey)!;
          (parentNode.children ?? (parentNode.children = [])).push(childNode);
        }

        parentMapKey = childMapKey;
      }
    }
    return roots;
  }
  // private buildHierarchyTree(cascadeData: any[], nameKey: string): any[] {
  //   const roots: any[] = [];
  //   const normalize = (val: string): string => (val ?? '').toLowerCase().split(' ').join('_');

  //   // Find actual key in item matching the normalized pattern (case-insensitive)
  //   const findKey = (item: any, pattern: string): string | undefined =>
  //     Object.keys(item).find(k => normalize(k) === normalize(pattern));

  //   cascadeData.forEach(item => {
  //     // S0 is the root level — find actual key e.g. 'Lob S0'
  //     const rootActualKey = findKey(item, `${nameKey} S0`);
  //     if (!rootActualKey || !item[rootActualKey]) return;

  //     const rootIdActualKey = findKey(item, `${nameKey} S0_Id`);
  //     const rootName = item[rootActualKey];
  //     const rootId = item[rootIdActualKey!] || item[rootActualKey];
  //     const rootValueNorm = normalize(rootId);

  //     let rootNode = roots.find(n => normalize(n.id) === rootValueNorm);
  //     if (!rootNode) {
  //       rootNode = { name: rootName, id: rootId };
  //       roots.push(rootNode);
  //     }

  //     let currentChildren: any[] = rootNode.children ?? (rootNode.children = []);
  //     let level = 1; // children start from S1
  //     while (true) {
  //       const levelActualKey = findKey(item, `${nameKey} S${level}`);
  //       if (!levelActualKey || !item[levelActualKey]) break;

  //       const levelIdActualKey = findKey(item, `${nameKey} S${level}_Id`);
  //       const childName = item[levelActualKey];
  //       const childId = item[levelIdActualKey!] || item[levelActualKey];
  //       const childValueNorm = normalize(childId);

  //       let childNode = currentChildren.find((n: any) => normalize(n.id) === childValueNorm);
  //       if (!childNode) {
  //         childNode = { name: childName, id: childId };
  //         currentChildren.push(childNode);
  //       }

  //       currentChildren = childNode.children ?? (childNode.children = []);
  //       level++;
  //     }
  //   });
  //   return roots;
  // }

  /** Resolves the actual cascade column backing a (non-hierarchal) filter. Must match
   *  the SAME column getFilterFieldsFromCascadeData() resolves as its "name" column,
   *  otherwise narrowRowsBySiblingSelection() below matches against the wrong column
   *  and silently narrows the running dataset to zero rows for every later sibling. */
  private resolveFilterColumnFromSample(sampleRow: any, filter: any): string | undefined {
    if (!sampleRow) return undefined;
    const rowKeys = Object.keys(sampleRow);
    const normName = String(filter?.name ?? '').toLowerCase();
    const exact = rowKeys.find(key => key.toLowerCase().split(' ').join('_') === normName);
    if (exact) return exact;
    // Fallback only when the primary (dropdown-builder) match fails.
    return rowKeys.find(k => this.norm(k) === this.norm(filter?.name))
      || (filter?.label ? rowKeys.find(k => this.norm(k) === this.norm(filter.label)) : undefined);
  }

  /** Narrows `rows` to those matching a multiselect filter's freshly-restored selection
   *  (plain value list, or a {__selectAll__:true[,__excludeNames]} sentinel). Used so a
   *  downstream sibling's rebuilt dropdown honors OTHER downstream filters that already
   *  hold a selection, not just the filters upstream of whichever filter the user changed. */
  private narrowRowsBySiblingSelection(rows: any[], filter: any, restoredValues: any[]): any[] {
    if (!Array.isArray(rows) || rows.length === 0 || !Array.isArray(restoredValues) || restoredValues.length === 0) {
      return rows;
    }

    const col = this.resolveFilterColumnFromSample(rows[0], filter);
    if (!col) return rows;

    const allSentinel = restoredValues.find((v: any) => v?.__selectAll__ === true);
    if (allSentinel) {
      const excludeNames: any[] = Array.isArray(allSentinel.__excludeNames) ? allSentinel.__excludeNames : [];
      if (excludeNames.length === 0) return rows;
      const excludeSet = new Set(excludeNames.map((n: any) => this.norm(String(n ?? ''))));
      return rows.filter((row: any) => !excludeSet.has(this.norm(String(row[col] ?? ''))));
    }

    const valueSet = new Set(restoredValues.map((v: any) => this.norm(String(v?.name ?? ''))));
    if (valueSet.size === 0) return rows;
    return rows.filter((row: any) => valueSet.has(this.norm(String(row[col] ?? ''))));
  }

  getFilterFieldsFromCascadeData(filter: any, filterType: string, cascadeData: any): Array<any> {
    if (!cascadeData || cascadeData.length === 0) return [];
    const filterName = filter.name
    if (filterType === 'hierarchal-delimiter') {
      return this.inbuiltUtilityService.buildTreeForHierarchyDelimiter(filter,cascadeData, String(filter?.splitClause ?? ''));
    }

    if (filterType === 'hierarchal') {
      // Build a deduplicated tree in a single pass — no per-item recursion
      const hierarchyColumns = this.getHierarchyColumnsFromFilter(filter);
      const keyparts = hierarchyColumns.displayKeys;
      const queryKeyParts = hierarchyColumns.queryKeys;
      const res =  this.buildHierarchyTree(cascadeData, keyparts, queryKeyParts);
      return res;
    }

    // Resolve column keys from first matching item: display key (nameKey) and value key (valueKey)
    const normName = filterName.toLowerCase();
    let nameKey: string | undefined;
    let valueKey: string | undefined;
    
    // If filter.queryKey is provided and differs from filter.name, use it as the value field lookup key
    const hasExplicitValueKey = !!(filter.queryKey && filter.queryKey !== filter.name);
    const normValueKey = hasExplicitValueKey && filter.queryKey ? filter.queryKey.toLowerCase() : undefined;
    
    for (const item of cascadeData) {
      if (!nameKey) {
        nameKey = Object.keys(item).find(key => key.toLowerCase().split(' ').join('_') === normName);
        if (nameKey) {
          if (hasExplicitValueKey) {
            // Use queryKey as the value field name: look for exact cascade field matching queryKey
            valueKey = Object.keys(item).find(key => key.toLowerCase().split(' ').join('_') === normValueKey) || nameKey;
          } else {
            // Fall back to existing logic: look for field ending in _id or use nameKey
            valueKey = Object.keys(item).find(key => key.toLowerCase() === (normName + '_id')) || nameKey;
          }
          break;
        }
      }
    }
    if (!nameKey) return [];

    // Use a Set for O(1) dedup instead of filter+findIndex O(n²)
    const seen = new Set<string>();
    const result: Array<any> = [];
    for (const item of cascadeData) {
      const val = item[nameKey];
      if (val !== undefined && val !== null && !seen.has(String(val))) {
        seen.add(String(val));
        result.push({ name: val, value: item[valueKey!] });
      }
    }
    return result;
  }

  filterCascadeDataForFields(limitToFilterNames?: Set<string>): void {
    const selectedFilters = this.selectedFilters ?? {};
    const rowFilterKeys = this.allFilters
      .map(f => f.name)
      .filter(name =>
        name !== 'attributes' &&
        name in selectedFilters &&
        (!limitToFilterNames || limitToFilterNames.has(name))
      );

    const hasActiveFilters = rowFilterKeys.some(
      key => selectedFilters[key]?.values?.length > 0
    );

    if (!hasActiveFilters || this.cascadeData.length === 0) {
      this.filteredCascadeData = [...this.cascadeData];
      return;
    }

    // Normalize helper: strip spaces/underscores/hyphens → lowercase

    // Build normalized column key → actual column name map ONCE from the first cascade row
    const columnKeyMap = new Map<string, string>();
    Object.keys(this.cascadeData[0]).forEach(col => columnKeyMap.set(this.norm(col), col));

    // Resolve filter name → actual cascade column using label (priority) then name.
    // Cascade column names match filter.label directly in most cases.
    const filterToColumnMap = new Map<string, string>();
    this.allFilters.forEach(f => {
      if (!f.name) return;
      const byLabel = f.label ? columnKeyMap.get(this.norm(f.label)) : undefined;
      if (byLabel) { filterToColumnMap.set(f.name, byLabel); return; }
      const byName = columnKeyMap.get(this.norm(f.name));
      if (byName) filterToColumnMap.set(f.name, byName);
    });

    // Pre-process each active filter into a fast-check structure:
    // - hierarchal: pre-normalize all path key-value pairs once, skip unmapped keys
    // - multiselect: build a Set of normalized values for O(1) per-row lookup
    const filterChecks = this.buildFilterChecks(rowFilterKeys, selectedFilters, columnKeyMap, filterToColumnMap);

    if (filterChecks.length === 0) {
      this.filteredCascadeData = [...this.cascadeData];
      return;
    }

    this.filteredCascadeData = this.cascadeData.filter((row: any) => this.rowPassesAllFilterChecks(row, filterChecks));
  }

  // hierarchal-delimiter values match either complete id equality or partial containment
  // (for example child-only value inside a parent-child composite id).
  private isDelimiterPartialOrCompleteMatch(rowValue: any, selectedValue: any): boolean {
    const rowText = (rowValue ?? '').toString().toLowerCase().trim();
    const selectedText = (selectedValue ?? '').toString().toLowerCase().trim();
    if (!rowText || !selectedText) return false;

    return rowText === selectedText || rowText.includes(selectedText) || selectedText.includes(rowText);
  }

  // AND across filters — every check must pass for the row to be included.
  private rowPassesAllFilterChecks(row: any, filterChecks: FilterCheck[]): boolean {
    for (const check of filterChecks) {
      if (!this.rowPassesFilterCheck(row, check)) return false;
    }
    return true;
  }

  // OR across selected paths, AND within each path (for hierarchal/-delimiter checks).
  private rowPassesFilterCheck(row: any, check: FilterCheck): boolean {
    switch (check.type) {
      case 'hierarchal':
        return check.paths.some(pairs =>
          pairs.every(([col, val]) => (row[col] ?? '').toString().toLowerCase().trim() === val)
        );
      case 'hierarchal-delimiter':
        return check.paths.some(pairs =>
          pairs.every(([col, val]) => this.isDelimiterPartialOrCompleteMatch(row[col], val))
        );
      case 'hierarchal-exclude':
        return !check.paths.some(pairs =>
          pairs.every(([col, val]) => (row[col] ?? '').toString().toLowerCase().trim() === val)
        );
      case 'hierarchal-delimiter-exclude':
        return !check.paths.some(pairs =>
          pairs.every(([col, val]) => this.isDelimiterPartialOrCompleteMatch(row[col], val))
        );
      case 'multiselect-exclude':
        return !check.excludeSet.has((row[check.col] ?? '').toString().toLowerCase().trim());
      case 'multiselect':
        return check.valueSet.has((row[check.col] ?? '').toString().toLowerCase().trim());
    }
  }

  // Pre-process each active filter into a fast-check structure used by filterCascadeDataForFields.
  private buildFilterChecks(
    rowFilterKeys: string[],
    selectedFilters: any,
    columnKeyMap: Map<string, string>,
    filterToColumnMap: Map<string, string>
  ): FilterCheck[] {
    const filterChecks: FilterCheck[] = [];

    for (const filterKey of rowFilterKeys) {
      const entry = selectedFilters[filterKey];
      const filterCheck = this.buildFilterCheck(filterKey, entry, columnKeyMap, filterToColumnMap);
      if (filterCheck) filterChecks.push(filterCheck);
    }

    return filterChecks;
  }

  private buildFilterCheck(
    filterKey: string,
    entry: any,
    columnKeyMap: Map<string, string>,
    filterToColumnMap: Map<string, string>
  ): FilterCheck | null {
    if (!Array.isArray(entry?.values) || entry.values.length === 0) return null;
    return this.isHierarchalType(entry.type)
      ? this.buildHierarchalFilterCheck(filterKey, entry, columnKeyMap)
      : this.buildMultiselectFilterCheck(filterKey, entry, filterToColumnMap);
  }

  private buildHierarchalFilterCheck(
    filterKey: string,
    entry: any,
    columnKeyMap: Map<string, string>
  ): FilterCheck | null {
    const hierarchyFilter = this.allFilters.find(filter => filter.name === filterKey);
    const idKeySet = this.getHierarchyIdKeySet(entry.type, hierarchyFilter);
    const buildPairs = (pathObj: any): Array<[string, string]> => this.buildFilterPathPairs(pathObj, idKeySet, columnKeyMap);
    const allSentinel = entry.values.find((value: any) => value?.__selectAll__ === true);

    if (allSentinel) {
      const excludePaths = (Array.isArray(allSentinel.__excludePaths) ? allSentinel.__excludePaths : [])
        .map((pathObj: any) => buildPairs(pathObj))
        .filter((paths: Array<[string, string]>) => paths.length > 0);
      if (excludePaths.length === 0) return null;
      const type = entry.type === 'hierarchal-delimiter'
        ? 'hierarchal-delimiter-exclude'
        : 'hierarchal-exclude';
      return { type, paths: excludePaths } as FilterCheck;
    }

    if (hierarchyFilter?.isSelectedAll && entry.values.length === 0) return null;

    const paths = entry.values
      .map((pathObj: any) => buildPairs(pathObj))
      .filter((path: Array<[string, string]>) => path.length > 0);
    if (paths.length === 0) return null;

    const type = entry.type === 'hierarchal-delimiter' ? 'hierarchal-delimiter' : 'hierarchal';
    return { type, paths } as FilterCheck;
  }

  private getHierarchyIdKeySet(type: string, hierarchyFilter: any): Set<string> | null {
    if ((type !== 'hierarchal' && type !== 'hierarchal-delimiter') || !hierarchyFilter) return null;
    const { queryKeys } = this.getHierarchyColumnsFromFilter(hierarchyFilter);
    return queryKeys.length > 0 ? new Set(queryKeys.map((key: string) => this.norm(key))) : null;
  }

  private buildFilterPathPairs(
    pathObj: any,
    idKeySet: Set<string> | null,
    columnKeyMap: Map<string, string>
  ): Array<[string, string]> {
    return Object.keys(pathObj)
      .filter(pathKey => !idKeySet || idKeySet.has(this.norm(pathKey)))
      .map(pathKey => {
        const actualColumn = columnKeyMap.get(this.norm(pathKey));
        if (!actualColumn) return null;
        return [actualColumn, (pathObj[pathKey] ?? '').toString().toLowerCase().trim()] as [string, string];
      })
      .filter((pair): pair is [string, string] => pair !== null);
  }

  private buildMultiselectFilterCheck(
    filterKey: string,
    entry: any,
    filterToColumnMap: Map<string, string>
  ): FilterCheck | null {
    const actualColumn = filterToColumnMap.get(filterKey);
    if (!actualColumn) return null;

    const allSentinel = entry.values.find((value: any) => value?.__selectAll__ === true);
    if (allSentinel) {
      const excludeNames = Array.isArray(allSentinel.__excludeNames) ? allSentinel.__excludeNames : [];
      if (excludeNames.length === 0) return null;
      const excludeSet = new Set<string>(
        excludeNames.map((name: any) => (name ?? '').toString().toLowerCase().trim())
      );
      return excludeSet.size > 0
        ? { type: 'multiselect-exclude', col: actualColumn, excludeSet }
        : null;
    }

    const valueSet = new Set<string>(
      entry.values.map((item: any) => (item.name ?? '').toString().toLowerCase().trim())
    );
    return valueSet.size > 0 ? { type: 'multiselect', col: actualColumn, valueSet } : null;
  }

  //Logic to filter cascade data based on selected filters which is coming in object form and each object contains array having (name as one of the property) selected values for that filter
  filterCascadeData() {
    this.filteredCascadeData = [];
    const filterKeys = Object.keys(this.selectedFilters);
    // 'attributes' is a column-selector only (selects which columns to display),
    // not a row filter — always exclude it from row-level filtering.
    // Order follows this.allFilters so upstream filters are applied before downstream
    // ones; keys not present in selectedFilters are skipped.
    const filterKeySet = new Set(filterKeys);
    const rowFilterKeys = this.allFilters
      .map(f => f.name)
      .filter(name => name !== 'attributes' && filterKeySet.has(name));

    // No active row filters → show full data
    const hasActiveFilters = rowFilterKeys.some(
      key => this.selectedFilters[key]?.values && this.selectedFilters[key].values.length > 0
    );

    if (!hasActiveFilters) {
      this.filteredCascadeData = [...this.cascadeData];
      return;
    }

    // Map filter.name → column name using filter.label directly (label matches column name as-is)
    const filterToColumnMap: { [filterName: string]: string } = {};
    this.allFilters.forEach(filter => {
      if (filter.name && filter.label) {
        filterToColumnMap[filter.name] = filter.label;
      }
    });

    this.filteredCascadeData = this.cascadeData.filter(row => {
      // AND across filters: every active filter must pass for the row to be included
      return rowFilterKeys.every(filterKey => {
        const filterEntry = this.selectedFilters[filterKey];
        const selectedItems: any[] = filterEntry?.values;

        // Empty selection → no restriction
        if (!selectedItems || selectedItems.length === 0) return true;

        // Hierarchal: path object keys are the filterKey segments which match column names directly
        if (this.isHierarchalType(filterEntry?.type)) {
          return selectedItems.some(pathObj => this.rowMatchesHierarchalPath(row, pathObj));
        }

        // Multiselect / standard filter: match by item.name against the label-resolved column
        const actualCol = filterToColumnMap[filterKey];
        if (!actualCol) return true;

        const rowValue = (row[actualCol] ?? '').toString().toLowerCase().trim();

        // OR within filter: row passes if it matches ANY selected item's name
        return selectedItems.some(
          item => (item.name ?? '').toString().toLowerCase().trim() === rowValue
        );
      });
    });
  }

  // Extracted so the row/filterKey/pathObj/pathKey callbacks in filterCascadeData()
  // don't nest more than 4 levels deep.
  private rowMatchesHierarchalPath(row: any, pathObj: any): boolean {
    return Object.keys(pathObj).every(pathKey => {
      if (!(pathKey in row)) return true; // column not present in row → skip
      const rowVal = (row[pathKey] ?? '').toString().toLowerCase().trim();
      const pathVal = (pathObj[pathKey] ?? '').toString().toLowerCase().trim();
      return rowVal === pathVal;
    });
  }

  onJsonEditorTextChange(ev: any) {
    const payload = {
      report: this.config?.reportNameFromQueryParams ?? '',
      Report_Type: 'inBuilt',
      Id: this.elementData?.element_id ?? null,
      element_json: ev || {},
    };
    this.reportsService.updateReportJSON(payload).subscribe({
      next: (res: any) => {
        this.toastr.success('Report JSON updated successfully', 'Success', { timeOut: 5000 , positionClass: 'toast-top-center' });
        const report = this.config?.reportNameFromQueryParams ?? '';
        const pageKey = this.config?.currentPage?.key ?? '';
        const tabKey = this.config?.currentTab?.key ?? '';
        const filterCacheKey = `${this.config.reportNameFromQueryParams}_${this.config.currentPage ? this.config.currentPage.key : ''}_${this.config.currentTab ? this.config.currentTab.key : ''}_data_filter_filter`;
        this.inbuiltUtilityService.removeCachedData(filterCacheKey);
        this.inbuiltUtilityService.removeCachedData(`${report}_${pageKey}_${tabKey}_data_page`);
        this.inbuiltUtilityService.removeCachedData(`${report}_${pageKey}_${tabKey}_data_tab`);
        this.inbuiltUtilityService.triggerRefreshChildData();
      },
      error: (err: any) => {
        this.toastr.error('Error while updating report JSON', 'Error', { timeOut: 5000 , positionClass: 'toast-top-center' });
      }
    });
  }
}

